import React, { useState, useEffect, useCallback, useRef } from 'react';
import { BrowserRouter as Router, Routes, Route, Link, useNavigate, useParams, useLocation } from 'react-router-dom';
import axios from 'axios';
import { jsPDF } from 'jspdf';
import './App.css';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

// Format time as HH:MM:SS
const formatTimer = (seconds) => {
  const h = Math.floor(seconds / 3600);
  const m = Math.floor((seconds % 3600) / 60);
  const s = seconds % 60;
  return `${h.toString().padStart(2, '0')}:${m.toString().padStart(2, '0')}:${s.toString().padStart(2, '0')}`;
};

// Format date
const formatDate = (dateStr) => {
  const date = new Date(dateStr + 'T00:00:00');
  return date.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' });
};

// Get week dates
const getWeekDates = (date = new Date()) => {
  const week = [];
  const start = new Date(date);
  start.setDate(start.getDate() - start.getDay());
  for (let i = 0; i < 7; i++) {
    const d = new Date(start);
    d.setDate(start.getDate() + i);
    week.push(d.toISOString().split('T')[0]);
  }
  return week;
};

// Subject colors and icons
const subjectColors = {
  'ELA': { bg: 'bg-blue-100', text: 'text-blue-700', dot: 'bg-blue-500', icon: '📖' },
  'Math': { bg: 'bg-purple-100', text: 'text-purple-700', dot: 'bg-purple-500', icon: '🔢' },
  'Science': { bg: 'bg-orange-100', text: 'text-orange-700', dot: 'bg-orange-500', icon: '🔬' },
  'Social Studies': { bg: 'bg-yellow-100', text: 'text-yellow-700', dot: 'bg-yellow-500', icon: '🌍' },
  'Art': { bg: 'bg-pink-100', text: 'text-pink-700', dot: 'bg-pink-500', icon: '🎨' },
  'Music': { bg: 'bg-indigo-100', text: 'text-indigo-700', dot: 'bg-indigo-500', icon: '🎵' },
  'PE': { bg: 'bg-green-100', text: 'text-green-700', dot: 'bg-green-500', icon: '⚽' },
  'Break': { bg: 'bg-amber-100', text: 'text-amber-700', dot: 'bg-amber-400', icon: '☕' },
  'Lunch': { bg: 'bg-emerald-100', text: 'text-emerald-700', dot: 'bg-emerald-400', icon: '🍎' },
  'Snack': { bg: 'bg-rose-100', text: 'text-rose-700', dot: 'bg-rose-400', icon: '🧃' },
  'Other': { bg: 'bg-gray-100', text: 'text-gray-700', dot: 'bg-gray-500', icon: '📝' },
};

// Active Student helpers (localStorage-based)
const getActiveStudentId = () => localStorage.getItem('activeStudentId') || null;
const setActiveStudentId = (id) => {
  if (id) localStorage.setItem('activeStudentId', id);
  else localStorage.removeItem('activeStudentId');
};

// Student avatar colors (consistent per student)
const studentAvatarColors = [
  { bg: 'bg-rose-400', text: 'text-white' },
  { bg: 'bg-sky-400', text: 'text-white' },
  { bg: 'bg-amber-400', text: 'text-white' },
  { bg: 'bg-emerald-400', text: 'text-white' },
  { bg: 'bg-violet-400', text: 'text-white' },
];

const getStudentColor = (index) => studentAvatarColors[index % studentAvatarColors.length];

// Compliance Status Badge
const ComplianceStatusBadge = ({ status }) => {
  const styles = {
    'On Track': 'bg-green-100 text-green-700',
    'Ahead': 'bg-blue-100 text-blue-700',
    'Behind': 'bg-red-100 text-red-700',
    'Unknown': 'bg-gray-100 text-gray-700'
  };
  return (
    <span className={`px-2 py-1 rounded-full text-xs font-medium ${styles[status] || styles['Unknown']}`}>
      {status}
    </span>
  );
};

// Compliance Card (for Today Dashboard)
const ComplianceCard = ({ compliance, onSetup }) => {
  if (!compliance) return null;

  if (!compliance.is_configured) {
    return (
      <div 
        data-testid="compliance-setup-prompt"
        className="bg-amber-50 border border-amber-200 rounded-xl p-4 mb-4"
      >
        <div className="flex items-start gap-3">
          <svg className="w-5 h-5 text-amber-500 mt-0.5 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
            <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
          </svg>
          <div className="flex-1">
            <p className="font-medium text-amber-800">Set Up Compliance Tracking</p>
            <p className="text-sm text-amber-700 mt-1">Configure your school year dates and target hours to track compliance.</p>
          </div>
          <button
            data-testid="setup-compliance-btn"
            onClick={onSetup}
            className="text-amber-700 font-medium text-sm hover:text-amber-800"
          >
            Set Up →
          </button>
        </div>
      </div>
    );
  }

  return (
    <div 
      data-testid="compliance-card"
      className="bg-white rounded-xl shadow-sm p-4 mb-4"
    >
      <div className="flex items-center justify-between mb-3">
        <h3 className="font-semibold text-gray-800">Year Compliance</h3>
        <ComplianceStatusBadge status={compliance.compliance_status} />
      </div>
      
      <div className="mb-3">
        <div className="flex justify-between text-sm mb-1">
          <span className="text-gray-600">{compliance.total_hours_logged}h logged</span>
          <span className="text-gray-500">{compliance.target_hours_total}h goal</span>
        </div>
        <div className="h-2 bg-gray-100 rounded-full overflow-hidden">
          <div 
            className={`h-full rounded-full transition-all ${
              compliance.compliance_status === 'Behind' ? 'bg-red-500' :
              compliance.compliance_status === 'Ahead' ? 'bg-blue-500' : 'bg-green-500'
            }`}
            style={{ width: `${Math.min(compliance.percent_complete, 100)}%` }}
          />
        </div>
        <p className="text-xs text-gray-400 mt-1">{compliance.percent_complete}% complete</p>
      </div>

      <div className="grid grid-cols-2 gap-3 text-center">
        <div className="bg-gray-50 rounded-lg p-2">
          <p className="text-lg font-bold text-gray-800">{compliance.remaining_hours}h</p>
          <p className="text-xs text-gray-500">remaining</p>
        </div>
        <div className="bg-gray-50 rounded-lg p-2">
          <p className="text-lg font-bold text-gray-800">{compliance.days_remaining}</p>
          <p className="text-xs text-gray-500">days left</p>
        </div>
      </div>

      {compliance.required_hours_per_day > 0 && (
        <p className="text-xs text-gray-500 mt-3 text-center">
          Need ~{compliance.required_hours_per_day}h/day to stay on track
        </p>
      )}
    </div>
  );
};

// School Year Card (compact card for bottom of Today dashboard - Stitch style)
const SchoolYearCard = ({ compliance, onSetup, onViewReport }) => {
  if (!compliance) return null;

  if (!compliance.is_configured) {
    return (
      <div 
        data-testid="school-year-setup-card"
        onClick={onSetup}
        className="bg-gradient-to-r from-indigo-500 to-purple-600 rounded-xl p-4 mb-4 cursor-pointer hover:from-indigo-600 hover:to-purple-700 transition-all"
      >
        <div className="flex items-center gap-4">
          <div className="w-12 h-12 rounded-full bg-white/20 flex items-center justify-center">
            <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" />
            </svg>
          </div>
          <div className="flex-1">
            <h3 className="text-white font-semibold">Your School Year</h3>
            <p className="text-white/80 text-sm">Set up compliance tracking</p>
          </div>
          <svg className="w-6 h-6 text-white/80" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
          </svg>
        </div>
      </div>
    );
  }

  return (
    <div 
      data-testid="school-year-card"
      onClick={onViewReport}
      className="bg-gradient-to-r from-indigo-500 to-purple-600 rounded-xl p-4 mb-4 cursor-pointer hover:from-indigo-600 hover:to-purple-700 transition-all"
    >
      <div className="flex items-center gap-4">
        <div className="w-12 h-12 rounded-full bg-white/20 flex items-center justify-center">
          <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" />
          </svg>
        </div>
        <div className="flex-1">
          <h3 className="text-white font-semibold">Your School Year</h3>
          <p className="text-white/80 text-sm">
            {compliance.total_hours_logged}h / {compliance.target_hours_total}h • {compliance.percent_complete}% complete
          </p>
        </div>
        <div className="flex items-center gap-2">
          <span className={`text-xs px-2 py-1 rounded-full font-medium ${
            compliance.compliance_status === 'Behind' ? 'bg-red-400/30 text-red-100' :
            compliance.compliance_status === 'Ahead' ? 'bg-green-400/30 text-green-100' : 
            'bg-green-400/30 text-green-100'
          }`}>
            {compliance.compliance_status}
          </span>
          <svg className="w-6 h-6 text-white/80" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
          </svg>
        </div>
      </div>
    </div>
  );
};

// Compliance Summary (for Reports)
const ComplianceSummary = ({ compliance, showDateFilter = true, onDateChange }) => {
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');

  if (!compliance?.is_configured) {
    return (
      <div className="bg-gray-50 border border-gray-200 rounded-xl p-4 mb-4">
        <p className="text-gray-500 text-sm">Compliance tracking not configured. Set up school year settings first.</p>
      </div>
    );
  }

  return (
    <div data-testid="compliance-summary" className="bg-white rounded-xl shadow-sm p-4 mb-4">
      <div className="flex items-center justify-between mb-4">
        <h3 className="font-semibold text-gray-800">Compliance Summary</h3>
        <ComplianceStatusBadge status={compliance.compliance_status} />
      </div>

      {showDateFilter && onDateChange && (
        <div className="flex gap-2 mb-4">
          <input
            type="date"
            value={startDate}
            onChange={(e) => { setStartDate(e.target.value); onDateChange(e.target.value, endDate); }}
            className="flex-1 border rounded-lg px-2 py-1 text-sm"
            placeholder="Start"
          />
          <input
            type="date"
            value={endDate}
            onChange={(e) => { setEndDate(e.target.value); onDateChange(startDate, e.target.value); }}
            className="flex-1 border rounded-lg px-2 py-1 text-sm"
            placeholder="End"
          />
        </div>
      )}

      <div className="grid grid-cols-2 gap-4 mb-4">
        <div className="text-center p-3 bg-indigo-50 rounded-lg">
          <p className="text-2xl font-bold text-indigo-600">{compliance.total_hours_logged}</p>
          <p className="text-xs text-gray-600">Hours Logged</p>
        </div>
        <div className="text-center p-3 bg-gray-50 rounded-lg">
          <p className="text-2xl font-bold text-gray-800">{compliance.target_hours_total}</p>
          <p className="text-xs text-gray-600">Target Hours</p>
        </div>
      </div>

      <div className="space-y-2 text-sm">
        <div className="flex justify-between">
          <span className="text-gray-600">Progress</span>
          <span className="font-medium">{compliance.percent_complete}%</span>
        </div>
        <div className="flex justify-between">
          <span className="text-gray-600">Remaining</span>
          <span className="font-medium">{compliance.remaining_hours} hours</span>
        </div>
        <div className="flex justify-between">
          <span className="text-gray-600">Days Left</span>
          <span className="font-medium">{compliance.days_remaining} days</span>
        </div>
        {compliance.required_hours_per_day > 0 && (
          <div className="flex justify-between">
            <span className="text-gray-600">Needed/Day</span>
            <span className="font-medium">{compliance.required_hours_per_day} hours</span>
          </div>
        )}
      </div>

      {/* Subject Breakdown */}
      {Object.keys(compliance.by_subject || {}).length > 0 && (
        <div className="mt-4 pt-4 border-t">
          <h4 className="font-medium text-gray-700 mb-2">By Subject</h4>
          <div className="space-y-1">
            {Object.entries(compliance.by_subject).map(([subject, minutes]) => (
              <div key={subject} className="flex justify-between text-sm">
                <span className="text-gray-600">{subject}</span>
                <span className="font-medium">{Math.round(minutes / 60 * 10) / 10}h</span>
              </div>
            ))}
          </div>
        </div>
      )}

      <p className="text-xs text-gray-400 mt-4">
        School Year: {compliance.date_range?.start} to {compliance.date_range?.end}
      </p>
    </div>
  );
};

// Alert Banner
const AlertBanner = ({ config, compact = false }) => {
  if (!config?.standards_is_partial) return null;
  if (compact) {
    return (
      <div data-testid="standards-alert-compact" className="bg-amber-50 text-amber-700 text-xs px-2 py-1 rounded">
        ⚠️ {config.standards_scope_note}
      </div>
    );
  }
  return (
    <div data-testid="standards-alert-banner" className="bg-amber-50 border-l-4 border-amber-400 text-amber-800 p-3 mb-4 rounded-r">
      <div className="flex items-center text-sm">
        <svg className="w-4 h-4 mr-2 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
          <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
        </svg>
        <span>{config.standards_scope_note}</span>
      </div>
    </div>
  );
};

// Bottom Navigation
const BottomNav = () => {
  const location = useLocation();
  const isActive = (path) => location.pathname === path || (path === '/' && location.pathname === '/');
  
  return (
    <nav className="fixed bottom-0 left-0 right-0 bg-white border-t border-gray-200 px-4 py-2 z-40">
      <div className="max-w-lg mx-auto flex justify-around">
        <Link to="/" className={`flex flex-col items-center py-1 px-3 ${isActive('/') ? 'text-indigo-600' : 'text-gray-500'}`}>
          <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 20 20">
            <path d="M5 3a2 2 0 00-2 2v2a2 2 0 002 2h2a2 2 0 002-2V5a2 2 0 00-2-2H5zM5 11a2 2 0 00-2 2v2a2 2 0 002 2h2a2 2 0 002-2v-2a2 2 0 00-2-2H5zM11 5a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2V5zM11 13a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2v-2z" />
          </svg>
          <span className="text-xs mt-1">Today</span>
        </Link>
        <Link to="/journal" className={`flex flex-col items-center py-1 px-3 ${isActive('/journal') ? 'text-indigo-600' : 'text-gray-500'}`}>
          <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" />
          </svg>
          <span className="text-xs mt-1">Journal</span>
        </Link>
        <Link to="/reports-list" className={`flex flex-col items-center py-1 px-3 ${isActive('/reports-list') ? 'text-indigo-600' : 'text-gray-500'}`}>
          <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
          </svg>
          <span className="text-xs mt-1">Reports</span>
        </Link>
        <Link to="/settings" className={`flex flex-col items-center py-1 px-3 ${isActive('/settings') ? 'text-indigo-600' : 'text-gray-500'}`}>
          <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
          </svg>
          <span className="text-xs mt-1">Settings</span>
        </Link>
      </div>
    </nav>
  );
};

// Timer Component
const Timer = ({ initialSeconds = 0, onTimeUpdate, isRunning, onToggle, onStop }) => {
  const [seconds, setSeconds] = useState(initialSeconds);
  const intervalRef = useRef(null);

  useEffect(() => {
    if (isRunning) {
      intervalRef.current = setInterval(() => {
        setSeconds(s => {
          const newS = s + 1;
          onTimeUpdate?.(newS);
          return newS;
        });
      }, 1000);
    } else {
      clearInterval(intervalRef.current);
    }
    return () => clearInterval(intervalRef.current);
  }, [isRunning, onTimeUpdate]);

  return (
    <div className="flex items-center gap-3">
      <span className="text-2xl font-mono text-indigo-600 font-bold">{formatTimer(seconds)}</span>
      <button
        onClick={onToggle}
        className={`p-2 rounded-full ${isRunning ? 'bg-amber-100 text-amber-600' : 'bg-indigo-100 text-indigo-600'}`}
      >
        {isRunning ? (
          <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 20 20">
            <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zM7 8a1 1 0 012 0v4a1 1 0 11-2 0V8zm5-1a1 1 0 00-1 1v4a1 1 0 102 0V8a1 1 0 00-1-1z" clipRule="evenodd" />
          </svg>
        ) : (
          <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 20 20">
            <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM9.555 7.168A1 1 0 008 8v4a1 1 0 001.555.832l3-2a1 1 0 000-1.664l-3-2z" clipRule="evenodd" />
          </svg>
        )}
      </button>
      {seconds > 0 && (
        <button
          onClick={() => onStop(Math.ceil(seconds / 60))}
          className="p-2 rounded-full bg-green-100 text-green-600"
        >
          <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 20 20">
            <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
          </svg>
        </button>
      )}
    </div>
  );
};

// Active Session Context - persists timer state across navigation
const ActiveSessionContext = React.createContext({
  activeSessions: [],
  refreshSessions: () => {},
  stopSession: () => {}
});

const useActiveSessions = () => React.useContext(ActiveSessionContext);

const ActiveSessionProvider = ({ children }) => {
  const [activeSessions, setActiveSessions] = useState([]);

  const refreshSessions = useCallback(async () => {
    try {
      const res = await axios.get(`${API_URL}/api/active-sessions`);
      setActiveSessions(res.data || []);
    } catch (err) {
      console.error('Error fetching active sessions:', err);
    }
  }, []);

  const stopSession = useCallback(async (sessionId, elapsedMinutes) => {
    try {
      await axios.post(`${API_URL}/api/active-sessions/stop-and-save/${sessionId}?elapsed_minutes=${elapsedMinutes}`);
      refreshSessions();
    } catch (err) {
      console.error('Error stopping session:', err);
    }
  }, [refreshSessions]);

  useEffect(() => {
    refreshSessions();
  }, [refreshSessions]);

  return (
    <ActiveSessionContext.Provider value={{ activeSessions, refreshSessions, stopSession }}>
      {children}
    </ActiveSessionContext.Provider>
  );
};

// Start Lesson Modal
const StartLessonModal = ({ students, templates, onClose, onStart, preselectedStudentId = null }) => {
  const [studentId, setStudentId] = useState(preselectedStudentId || '');
  const [subject, setSubject] = useState('ELA');
  const [title, setTitle] = useState('');
  const [notes, setNotes] = useState('');
  const [templateId, setTemplateId] = useState('');
  const [conflictSession, setConflictSession] = useState(null);
  const [loading, setLoading] = useState(false);

  const handleTemplateChange = (tid) => {
    setTemplateId(tid);
    if (tid) {
      const template = templates.find(t => t.id === tid);
      if (template) {
        setSubject(template.subject || 'ELA');
        setTitle(template.default_title || '');
        setNotes(template.default_notes || '');
      }
    }
  };

  const handleSubmit = async (forceStart = false) => {
    if (!studentId) return;
    setLoading(true);
    try {
      const params = new URLSearchParams();
      params.append('student_id', studentId);
      params.append('subject', subject);
      if (title) params.append('title', title);
      if (notes) params.append('notes', notes);
      if (templateId) params.append('template_id', templateId);

      const res = await axios.post(`${API_URL}/api/start-lesson?${params}`);
      
      if (res.data.conflict && !forceStart) {
        setConflictSession(res.data.existing_session);
        setLoading(false);
        return;
      }

      onStart(res.data);
    } catch (err) {
      console.error('Error starting lesson:', err);
      setLoading(false);
    }
  };

  const handleStopAndStart = async () => {
    if (!conflictSession) return;
    setLoading(true);
    try {
      // Calculate elapsed time
      const startedAt = new Date(conflictSession.started_at);
      const elapsed = Math.ceil((Date.now() - startedAt.getTime()) / 60000);
      
      // Stop existing session
      await axios.post(`${API_URL}/api/active-sessions/stop-and-save/${conflictSession.id}?elapsed_minutes=${elapsed}`);
      
      // Clear conflict and retry
      setConflictSession(null);
      handleSubmit(true);
    } catch (err) {
      console.error('Error stopping session:', err);
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-end sm:items-center justify-center z-50" onClick={onClose}>
      <div 
        data-testid="start-lesson-modal" 
        className="bg-white rounded-t-2xl sm:rounded-2xl shadow-xl p-6 w-full sm:max-w-md"
        onClick={e => e.stopPropagation()}
      >
        {conflictSession ? (
          <div>
            <h3 className="text-lg font-semibold mb-2 text-amber-600">⚠️ Lesson in Progress</h3>
            <p className="text-gray-600 mb-4">This student already has an active lesson. Stop the current lesson and start a new one?</p>
            <div className="flex gap-3">
              <button onClick={onClose} className="flex-1 py-2 text-gray-600 hover:bg-gray-100 rounded-lg">Cancel</button>
              <button 
                data-testid="stop-and-start-btn"
                onClick={handleStopAndStart} 
                disabled={loading}
                className="flex-1 py-2 bg-amber-500 hover:bg-amber-600 text-white rounded-lg disabled:bg-gray-300"
              >
                {loading ? 'Stopping...' : 'Stop & Start New'}
              </button>
            </div>
          </div>
        ) : (
          <>
            <h3 className="text-lg font-semibold mb-4">Start Lesson</h3>
            <div className="space-y-4">
              {preselectedStudentId ? (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Student</label>
                  <div className="w-full border rounded-lg px-3 py-2 bg-gray-50 text-gray-700">
                    {students.find(s => s.id === preselectedStudentId)?.first_name || 'Selected'}
                  </div>
                </div>
              ) : (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Student *</label>
                  <select
                    data-testid="lesson-student-select"
                    value={studentId}
                    onChange={(e) => setStudentId(e.target.value)}
                    className="w-full border rounded-lg px-3 py-2"
                    required
                  >
                    <option value="">Select student...</option>
                    {students.map(s => (
                      <option key={s.id} value={s.id}>{s.first_name} {s.last_name}</option>
                    ))}
                  </select>
                </div>
              )}

              {templates.length > 0 && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Use Template</label>
                  <select
                    data-testid="lesson-template-select"
                    value={templateId}
                    onChange={(e) => handleTemplateChange(e.target.value)}
                    className="w-full border rounded-lg px-3 py-2"
                  >
                    <option value="">No template</option>
                    {templates.map(t => (
                      <option key={t.id} value={t.id}>{t.name} ({t.subject}, {t.default_duration || 30}min)</option>
                    ))}
                  </select>
                </div>
              )}

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Subject</label>
                <select
                  data-testid="lesson-subject-select"
                  value={subject}
                  onChange={(e) => setSubject(e.target.value)}
                  className="w-full border rounded-lg px-3 py-2"
                >
                  <option value="ELA">ELA</option>
                  <option value="Math">Math</option>
                  <option value="Science">Science</option>
                  <option value="Social Studies">Social Studies</option>
                  <option value="Other">Other</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Title (optional)</label>
                <input
                  data-testid="lesson-title-input"
                  type="text"
                  value={title}
                  onChange={(e) => setTitle(e.target.value)}
                  placeholder="e.g., Reading Chapter 5"
                  className="w-full border rounded-lg px-3 py-2"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Notes (optional)</label>
                <textarea
                  data-testid="lesson-notes-input"
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  rows={2}
                  className="w-full border rounded-lg px-3 py-2"
                />
              </div>
            </div>

            <div className="flex gap-3 mt-6">
              <button onClick={onClose} className="flex-1 py-2 text-gray-600 hover:bg-gray-100 rounded-lg">Cancel</button>
              <button 
                data-testid="start-lesson-submit-btn"
                onClick={() => handleSubmit(false)} 
                disabled={!studentId || loading}
                className="flex-1 py-3 bg-green-500 hover:bg-green-600 text-white rounded-lg font-semibold disabled:bg-gray-300 flex items-center justify-center gap-2"
              >
                {loading ? 'Starting...' : (
                  <>
                    <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM9.555 7.168A1 1 0 008 8v4a1 1 0 001.555.832l3-2a1 1 0 000-1.664l-3-2z" clipRule="evenodd" />
                    </svg>
                    Start Lesson
                  </>
                )}
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

// Active Lesson Banner (shows when a lesson is in progress)
const ActiveLessonBanner = ({ session, student, onNavigate, onStop }) => {
  const [elapsed, setElapsed] = useState(0);
  const startedAt = new Date(session.started_at);

  useEffect(() => {
    const interval = setInterval(() => {
      setElapsed(Math.floor((Date.now() - startedAt.getTime()) / 1000));
    }, 1000);
    return () => clearInterval(interval);
  }, [startedAt]);

  const handleStop = () => {
    onStop(session.id, Math.ceil(elapsed / 60));
  };

  return (
    <div 
      data-testid="active-lesson-banner"
      className="bg-green-500 text-white px-4 py-3 flex items-center justify-between cursor-pointer"
      onClick={() => onNavigate(session.work_item_id)}
    >
      <div className="flex items-center gap-3">
        <div className="w-3 h-3 bg-white rounded-full animate-pulse" />
        <div>
          <p className="font-medium text-sm">{student?.first_name}'s Lesson</p>
          <p className="text-xs text-green-100">LIVE • {formatTimer(elapsed)}</p>
        </div>
      </div>
      <button 
        data-testid="stop-lesson-btn"
        onClick={(e) => { e.stopPropagation(); handleStop(); }}
        className="bg-white text-green-600 px-3 py-1 rounded-lg text-sm font-medium hover:bg-green-50"
      >
        Stop
      </button>
    </div>
  );
};

// Quick Add Work Item
const QuickAddWorkItem = ({ studentId, studentName, onAdd, onCancel }) => {
  const today = new Date().toISOString().split('T')[0];
  const [title, setTitle] = useState('');
  const [subject, setSubject] = useState('ELA');
  const [useTimer, setUseTimer] = useState(false);
  const [timerRunning, setTimerRunning] = useState(false);
  const [timerSeconds, setTimerSeconds] = useState(0);
  const [manualMinutes, setManualMinutes] = useState(30);

  const handleSubmit = () => {
    if (!title.trim()) return;
    const duration = useTimer ? Math.ceil(timerSeconds / 60) : manualMinutes;
    onAdd({
      student_id: studentId,
      title,
      date: today,
      duration_minutes: duration || 1,
      subject,
      notes: ''
    });
  };

  const handleTimerStop = (minutes) => {
    setTimerRunning(false);
    setManualMinutes(minutes);
    setUseTimer(false);
  };

  return (
    <div data-testid="quick-add-form" className="bg-white rounded-xl shadow-lg p-4 mb-4 border-l-4 border-indigo-500">
      <div className="flex items-center justify-between mb-3">
        <h3 className="font-semibold text-gray-800">Quick Add for {studentName}</h3>
        <button onClick={onCancel} className="text-gray-400 hover:text-gray-600">
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>
      </div>
      
      <div className="space-y-3">
        <input
          data-testid="quick-add-title"
          type="text"
          placeholder="What did they learn? (e.g., Reading Chapter 5)"
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          className="w-full border border-gray-200 rounded-lg px-3 py-2 focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
          autoFocus
        />
        
        <div className="flex gap-2">
          <select
            value={subject}
            onChange={(e) => setSubject(e.target.value)}
            className="border border-gray-200 rounded-lg px-3 py-2 focus:ring-2 focus:ring-indigo-500"
          >
            <option value="ELA">ELA</option>
            <option value="Math">Math</option>
            <option value="Science">Science</option>
            <option value="Social Studies">Social Studies</option>
            <option value="Other">Other</option>
          </select>
          
          {!useTimer ? (
            <div className="flex items-center gap-2 flex-1">
              <input
                type="number"
                value={manualMinutes}
                onChange={(e) => setManualMinutes(parseInt(e.target.value) || 0)}
                className="w-20 border border-gray-200 rounded-lg px-3 py-2"
                min="1"
              />
              <span className="text-gray-500 text-sm">min</span>
              <button
                onClick={() => setUseTimer(true)}
                className="ml-auto text-indigo-600 text-sm font-medium hover:text-indigo-700"
              >
                Use Timer
              </button>
            </div>
          ) : (
            <div className="flex-1">
              <Timer
                initialSeconds={timerSeconds}
                onTimeUpdate={setTimerSeconds}
                isRunning={timerRunning}
                onToggle={() => setTimerRunning(!timerRunning)}
                onStop={handleTimerStop}
              />
            </div>
          )}
        </div>
        
        <button
          data-testid="quick-add-submit"
          onClick={handleSubmit}
          disabled={!title.trim() || (useTimer && timerRunning)}
          className="w-full bg-indigo-600 hover:bg-indigo-700 disabled:bg-gray-300 text-white py-2 rounded-lg font-medium transition-colors"
        >
          {useTimer && timerRunning ? 'Stop timer to save' : 'Add Activity'}
        </button>
      </div>
    </div>
  );
};

// Today Page (Dashboard)
const TodayPage = () => {
  const [students, setStudents] = useState([]);
  const [workItems, setWorkItems] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [config, setConfig] = useState(null);
  const [compliance, setCompliance] = useState(null);
  const [loading, setLoading] = useState(true);
  const [quickAddStudent, setQuickAddStudent] = useState(null);
  const [showAddStudent, setShowAddStudent] = useState(false);
  const [showStartLesson, setShowStartLesson] = useState(false);
  const [activeSessions, setActiveSessions] = useState([]);
  const [activeStudentId, setActiveStudentIdState] = useState(getActiveStudentId());
  const navigate = useNavigate();

  // Session Tracking State (local)
  const [localSessions, setLocalSessions] = useState(() => {
    const saved = localStorage.getItem('homested_sessions');
    return saved ? JSON.parse(saved) : [];
  });
  const [activeLocalSession, setActiveLocalSession] = useState(null);
  const [showSessionView, setShowSessionView] = useState(false);
  const [showTodayActivity, setShowTodayActivity] = useState(false);
  const [sessionNotes, setSessionNotes] = useState('');

  // Session Context Functions
  const startSession = () => {
    const newSession = {
      id: `session_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
      status: 'active',
      startTime: new Date().toISOString(),
      notes: '',
      minutes: null,
      evidence: [],
      date: new Date().toISOString().split('T')[0]
    };
    setActiveLocalSession(newSession);
    setSessionNotes('');
    setShowSessionView(true);
  };

  const saveSession = () => {
    if (activeLocalSession) {
      const updatedSession = {
        ...activeLocalSession,
        status: 'saved',
        notes: sessionNotes,
        savedAt: new Date().toISOString()
      };
      const updatedSessions = [updatedSession, ...localSessions.filter(s => s.id !== updatedSession.id)];
      setLocalSessions(updatedSessions);
      localStorage.setItem('homested_sessions', JSON.stringify(updatedSessions));
      setActiveLocalSession(null);
      setShowSessionView(false);
    }
  };

  const loadSession = (id) => {
    const session = localSessions.find(s => s.id === id);
    if (session) {
      setActiveLocalSession(session);
      setSessionNotes(session.notes || '');
      setShowSessionView(true);
      setShowTodayActivity(false);
    }
  };

  const getTodaySessions = () => {
    const todayStr = new Date().toISOString().split('T')[0];
    return localSessions.filter(s => s.date === todayStr).sort((a, b) => 
      new Date(b.startTime) - new Date(a.startTime)
    );
  };

  const hasActivityToday = () => {
    const todayStr = new Date().toISOString().split('T')[0];
    const todayWorkItems = workItems.filter(w => w.date === todayStr);
    const todaySessions = localSessions.filter(s => s.date === todayStr);
    return todayWorkItems.length > 0 || todaySessions.length > 0;
  };

  const handleSetActiveStudent = (id) => {
    setActiveStudentId(id);
    setActiveStudentIdState(id);
  };

  const today = new Date().toISOString().split('T')[0];
  const weekDates = getWeekDates();
  
  // Date display helper
  const getTodayLabel = () => {
    const d = new Date();
    const dayName = d.toLocaleDateString('en-US', { weekday: 'long' });
    const dateStr = d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
    return `${dayName} • ${dateStr}`;
  };

  const fetchData = useCallback(async () => {
    try {
      const [studentsRes, workItemsRes, configRes, templatesRes, sessionsRes, complianceRes] = await Promise.all([
        axios.get(`${API_URL}/api/students`),
        axios.get(`${API_URL}/api/work-items`),
        axios.get(`${API_URL}/api/config`),
        axios.get(`${API_URL}/api/templates`),
        axios.get(`${API_URL}/api/active-sessions`),
        axios.get(`${API_URL}/api/compliance`)
      ]);
      setStudents(studentsRes.data);
      setWorkItems(workItemsRes.data);
      setConfig(configRes.data);
      setTemplates(templatesRes.data);
      setActiveSessions(sessionsRes.data || []);
      setCompliance(complianceRes.data);
    } catch (err) {
      console.error('Error:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleQuickAdd = async (data) => {
    try {
      await axios.post(`${API_URL}/api/work-items`, data);
      setQuickAddStudent(null);
      fetchData();
    } catch (err) {
      console.error('Error:', err);
    }
  };

  const handleAddStudent = async (data) => {
    try {
      await axios.post(`${API_URL}/api/students`, data);
      setShowAddStudent(false);
      fetchData();
    } catch (err) {
      console.error('Error:', err);
    }
  };

  const handleStartLesson = (result) => {
    setShowStartLesson(false);
    if (result.work_item) {
      navigate(`/work-items/${result.work_item.id}`);
    }
    fetchData();
  };

  const handleStopSession = async (sessionId, elapsedMinutes) => {
    try {
      await axios.post(`${API_URL}/api/active-sessions/stop-and-save/${sessionId}?elapsed_minutes=${elapsedMinutes}`);
      fetchData();
    } catch (err) {
      console.error('Error stopping session:', err);
    }
  };

  const todayItems = workItems.filter(w => w.date === today);
  const todayMinutes = todayItems.reduce((sum, w) => sum + w.duration_minutes, 0);
  const weeklyData = weekDates.map(date => ({
    date,
    day: new Date(date + 'T00:00:00').toLocaleDateString('en-US', { weekday: 'short' }),
    minutes: workItems.filter(w => w.date === date).reduce((sum, w) => sum + w.duration_minutes, 0)
  }));
  const maxWeekMinutes = Math.max(...weeklyData.map(d => d.minutes), 60);

  if (loading) return <div className="min-h-screen bg-gray-50 flex items-center justify-center">Loading...</div>;

  // Get today's greeting based on time
  const getGreeting = () => {
    const hour = new Date().getHours();
    if (hour < 12) return 'Good morning';
    if (hour < 17) return 'Good afternoon';
    return 'Good evening';
  };

  // Get subheading with date
  const getSubheading = () => {
    return getTodayLabel();
  };

  // Format time for schedule display
  const formatScheduleTime = (dateStr) => {
    return new Date(dateStr).toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' });
  };

  // Find student index for consistent avatar color
  const getStudentIndex = (studentId) => students.findIndex(s => s.id === studentId);

  return (
    <div data-testid="today-page" className="min-h-screen bg-gray-50 pb-20">
      {/* Active Lesson Banners */}
      {activeSessions.map(session => {
        const student = students.find(s => s.id === session.student_id);
        return (
          <ActiveLessonBanner
            key={session.id}
            session={session}
            student={student}
            onNavigate={(workItemId) => navigate(`/work-items/${workItemId}`)}
            onStop={handleStopSession}
          />
        );
      })}

      {/* Header */}
      <header className="bg-indigo-600 text-white px-4 py-4 pb-6">
        <div className="flex items-center justify-between mb-2">
          <h1 className="text-xl font-bold">Homested</h1>
          <div className="flex items-center gap-1">
            {students.map((s, i) => {
              const color = getStudentColor(i);
              const isActive = activeStudentId === s.id;
              return (
                <button 
                  key={s.id}
                  onClick={() => handleSetActiveStudent(isActive ? null : s.id)}
                  className={`w-8 h-8 rounded-full ${color.bg} ${color.text} flex items-center justify-center text-sm font-medium shadow-sm transition-all ${isActive ? 'ring-2 ring-white ring-offset-2 ring-offset-indigo-600' : 'opacity-70 hover:opacity-100'}`}
                  style={{ marginLeft: i > 0 ? '-6px' : 0, zIndex: isActive ? 10 : students.length - i }}
                  title={isActive ? `${s.first_name} (active)` : `Switch to ${s.first_name}`}
                >
                  {s.first_name[0]}
                </button>
              );
            })}
          </div>
        </div>
        <p className="text-lg font-medium">{getGreeting()}</p>
        <p className="text-sm text-indigo-200">{getSubheading()}</p>
      </header>

      <main className="px-4 -mt-2">
        {/* Session Tracking Card - PRIMARY */}
        <div className="bg-white rounded-xl shadow-sm p-4 mb-4 border border-gray-100">
          {hasActivityToday() ? (
            // Activity exists today
            <div className="text-center py-2">
              <div className="w-10 h-10 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-3">
                <svg className="w-5 h-5 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
              </div>
              <h3 className="font-semibold text-gray-800 mb-1">Learning logged today</h3>
              <p className="text-sm text-gray-500 mb-4">Nice work — this activity will be included in your records and reports.</p>
              <button
                data-testid="view-today-activity-btn"
                onClick={() => setShowTodayActivity(true)}
                className="w-full bg-indigo-600 hover:bg-indigo-700 text-white py-3 rounded-xl font-semibold mb-2"
              >
                View today's activity
              </button>
              <button
                data-testid="start-another-session-top-btn"
                onClick={startSession}
                className="w-full border-2 border-indigo-200 text-indigo-600 py-2.5 rounded-xl font-semibold hover:bg-indigo-50 mb-2"
              >
                Start another session
              </button>
              <div className="flex gap-2 p-2 bg-indigo-50 rounded-xl">
                <button 
                  data-testid="log-activity-btn"
                  onClick={() => setShowStartLesson(true)}
                  className="flex-1 bg-white hover:bg-indigo-100 text-indigo-700 py-2.5 rounded-lg text-sm font-medium border border-indigo-200 flex items-center justify-center gap-1.5 shadow-sm"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                  </svg>
                  Log activity
                </button>
                <button 
                  data-testid="upload-work-btn"
                  onClick={() => navigate('/journal')}
                  className="flex-1 bg-white hover:bg-indigo-100 text-indigo-700 py-2.5 rounded-lg text-sm font-medium border border-indigo-200 flex items-center justify-center gap-1.5 shadow-sm"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-8l-4-4m0 0L8 8m4-4v12" />
                  </svg>
                  Upload work
                </button>
              </div>
            </div>
          ) : (
            // No activity today
            <div className="text-center py-2">
              <div className="w-10 h-10 bg-indigo-100 rounded-full flex items-center justify-center mx-auto mb-3">
                <svg className="w-5 h-5 text-indigo-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" />
                </svg>
              </div>
              <h3 className="font-semibold text-gray-800 mb-1">Ready to capture learning?</h3>
              <p className="text-sm text-gray-500 mb-4">Learning can happen anytime. Start a session or log completed work when you're ready.</p>
              <button
                data-testid="start-session-btn-primary"
                onClick={startSession}
                className="w-full bg-indigo-600 hover:bg-indigo-700 text-white py-3 rounded-xl font-semibold mb-2"
              >
                Start session
              </button>
              <div className="flex gap-2 p-2 bg-indigo-50 rounded-xl">
                <button 
                  data-testid="log-activity-btn-empty"
                  onClick={() => setShowStartLesson(true)}
                  className="flex-1 bg-white hover:bg-indigo-100 text-indigo-700 py-2.5 rounded-lg text-sm font-medium border border-indigo-200 flex items-center justify-center gap-1.5 shadow-sm"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                  </svg>
                  Log activity
                </button>
                <button 
                  data-testid="upload-work-btn-empty"
                  onClick={() => navigate('/journal')}
                  className="flex-1 bg-white hover:bg-indigo-100 text-indigo-700 py-2.5 rounded-lg text-sm font-medium border border-indigo-200 flex items-center justify-center gap-1.5 shadow-sm"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-8l-4-4m0 0L8 8m4-4v12" />
                  </svg>
                  Upload work
                </button>
              </div>
            </div>
          )}
        </div>

        {/* Start Lesson Button */}
        {students.length > 0 && activeSessions.length === 0 && (
          <button
            data-testid="start-lesson-btn"
            onClick={() => setShowStartLesson(true)}
            className="w-full bg-green-500 hover:bg-green-600 text-white py-4 rounded-xl font-semibold mb-4 flex items-center justify-center gap-2 shadow-lg"
          >
            <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM9.555 7.168A1 1 0 008 8v4a1 1 0 001.555.832l3-2a1 1 0 000-1.664l-3-2z" clipRule="evenodd" />
            </svg>
            Start Lesson
          </button>
        )}

        {/* Today's Schedule - Secondary visual */}
        {todayItems.length > 0 && (
          <div className="bg-gray-50 rounded-xl p-4 mb-4 border border-gray-100">
            <p className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-3">Today's logged activities</p>
            <div className="space-y-2">
              {todayItems.map((item) => {
                const student = students.find(s => s.id === item.student_id);
                const studentIdx = getStudentIndex(item.student_id);
                const avatarColor = getStudentColor(studentIdx);
                const isActive = activeSessions.some(s => s.work_item_id === item.id);
                
                return (
                  <div 
                    key={item.id}
                    data-testid={`today-item-${item.id}`}
                        className={`flex items-center gap-3 py-2 px-3 bg-white rounded-lg cursor-pointer ${
                          isActive ? 'ring-2 ring-green-400' : ''
                        }`}
                        onClick={() => navigate(`/work-items/${item.id}`)}
                      >
                        <div className={`w-7 h-7 rounded-full ${avatarColor.bg} ${avatarColor.text} flex items-center justify-center font-semibold text-xs flex-shrink-0`}>
                          {student?.first_name?.[0] || '?'}
                        </div>
                        <span className="text-sm text-gray-600 flex-1">
                          {student?.first_name} — {item.subject}
                        </span>
                        {isActive && (
                          <span className="px-2 py-0.5 bg-green-500 text-white text-[10px] rounded-full animate-pulse font-medium">LIVE</span>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            {/* Planned Schedule - Secondary Visual */}
            {students.length > 0 && (
              <div className="bg-white rounded-xl shadow-sm p-4 mb-4 opacity-80">
                <details>
                  <summary className="flex justify-between items-center cursor-pointer">
                    <h2 className="font-semibold text-gray-700 text-sm">Planned Schedule</h2>
                    <svg className="w-5 h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                    </svg>
                  </summary>
                  <div className="mt-4">
                    {todayItems.length === 0 ? (
                      <p className="text-gray-400 text-sm text-center py-4">No activities logged yet</p>
                    ) : (
                      <div>
                        {/* Parallel-by-Student Schedule Layout */}
                        {(() => {
                          // School day constants
                          const SCHOOL_DAY_START = 9 * 60;  // 9:00 AM in minutes
                          const SCHOOL_DAY_END = 15 * 60;   // 3:00 PM in minutes
                          const MIN_SEGMENT = 5;
                          
                          // Fixed breaks
                          const BREAKS = [
                            { id: 'snack', name: 'Snack', icon: '🍎', start: 10 * 60 + 15, end: 10 * 60 + 30 },
                            { id: 'lunch', name: 'Lunch', icon: '🥪', start: 12 * 60, end: 12 * 60 + 30 },
                            { id: 'break', name: 'Break', icon: '☕', start: 14 * 60 + 15, end: 14 * 60 + 25 },
                          ];
                          
                          // Format time
                          const formatTime = (mins) => {
                            const h = Math.floor(mins / 60);
                            const m = mins % 60;
                            const period = h >= 12 ? 'PM' : 'AM';
                            const displayH = h > 12 ? h - 12 : h === 0 ? 12 : h;
                            return `${displayH}:${m.toString().padStart(2, '0')} ${period}`;
                          };
                          
                          // Get students with activities today
                          const activeStudentIds = [...new Set(todayItems.map(i => i.student_id))];
                          const activeStudents = students.filter(s => activeStudentIds.includes(s.id));
                          
                          // Build schedule per student
                  const buildStudentSchedule = (studentId) => {
                    const items = todayItems.filter(i => i.student_id === studentId);
                    const schedule = [];
                    let currentTime = SCHOOL_DAY_START;
                    const usedBreaks = new Set();
                    
                    items.forEach((item) => {
                      let remaining = item.duration_minutes || 0;
                      
                      while (remaining > 0 && currentTime < SCHOOL_DAY_END) {
                        // Check if inside a break
                        const inBreak = BREAKS.find(b => currentTime >= b.start && currentTime < b.end);
                        if (inBreak) {
                          if (!usedBreaks.has(inBreak.id)) {
                            schedule.push({ type: 'break', ...inBreak });
                            usedBreaks.add(inBreak.id);
                          }
                          currentTime = inBreak.end;
                          continue;
                        }
                        
                        // Find next break
                        const nextBreak = BREAKS.find(b => b.start > currentTime && b.start < currentTime + remaining);
                        
                        if (nextBreak) {
                          const segDuration = nextBreak.start - currentTime;
                          if (segDuration >= MIN_SEGMENT) {
                            schedule.push({
                              type: 'lesson', item,
                              start: currentTime, end: nextBreak.start,
                            });
                          }
                          remaining -= segDuration;
                          if (!usedBreaks.has(nextBreak.id)) {
                            schedule.push({ type: 'break', ...nextBreak });
                            usedBreaks.add(nextBreak.id);
                          }
                          currentTime = nextBreak.end;
                        } else {
                          const segEnd = Math.min(currentTime + remaining, SCHOOL_DAY_END);
                          if (segEnd - currentTime >= MIN_SEGMENT) {
                            schedule.push({
                              type: 'lesson', item,
                              start: currentTime, end: segEnd,
                            });
                          }
                          currentTime = segEnd;
                          remaining = 0;
                        }
                      }
                    });
                    
                    // Add remaining breaks
                    BREAKS.forEach(b => {
                      if (!usedBreaks.has(b.id)) schedule.push({ type: 'break', ...b });
                    });
                    
                    return schedule.sort((a, b) => a.start - b.start);
                  };
                  
                  // Consolidate contiguous blocks (same subject, no break between)
                  const consolidateSchedule = (schedule) => {
                    const consolidated = [];
                    
                    schedule.forEach((item) => {
                      if (item.type === 'break') {
                        // Breaks always appear as their own items and reset grouping
                        consolidated.push(item);
                        return;
                      }
                      
                      // Check if we can merge with the previous lesson (same SUBJECT, contiguous)
                      const lastItem = consolidated[consolidated.length - 1];
                      if (
                        lastItem &&
                        lastItem.type === 'lesson' &&
                        lastItem.item.subject === item.item.subject &&
                        lastItem.end === item.start
                      ) {
                        // Extend the previous block's end time
                        lastItem.end = item.end;
                        // Keep reference to first item for navigation
                      } else {
                        // Add as new block
                        consolidated.push({ ...item });
                      }
                    });
                    
                    return consolidated;
                  };
                  
                  // Build schedules for each student with consolidation
                  const studentSchedules = activeStudents.map(s => ({
                    student: s,
                    schedule: consolidateSchedule(buildStudentSchedule(s.id)),
                  }));
                  
                  // Get all unique time slots for breaks (to render as full-width bands)
                  const allBreakTimes = BREAKS.map(b => ({ ...b, start: b.start, end: b.end }));
                  
                  // Render parallel lanes
                  return (
                    <div>
                      {/* Student lane headers */}
                      {activeStudents.length > 1 && (
                        <div className="flex gap-2 mb-3">
                          {activeStudents.map((s, idx) => {
                            const color = getStudentColor(idx);
                            return (
                              <div key={s.id} className="flex-1 flex items-center gap-2 px-2 py-1.5 bg-gray-50 rounded-lg">
                                <div className={`w-6 h-6 rounded-full ${color.bg} ${color.text} flex items-center justify-center text-xs font-semibold`}>
                                  {s.first_name[0]}
                                </div>
                                <span className="text-sm font-medium text-gray-700">{s.first_name}</span>
                              </div>
                            );
                          })}
                        </div>
                      )}
                      
                      {/* Stitch-style Flow Layout */}
                      {activeStudents.length > 1 ? (
                        <div className="space-y-4">
                          {/* Build sections separated by breaks */}
                          {(() => {
                            // Define time sections based on breaks
                            const sections = [
                              { id: 'morning', start: SCHOOL_DAY_START, end: BREAKS[0].start }, // Before Snack
                              { id: 'snack', type: 'break', break: BREAKS[0] },
                              { id: 'mid-morning', start: BREAKS[0].end, end: BREAKS[1].start }, // After Snack, Before Lunch
                              { id: 'lunch', type: 'break', break: BREAKS[1] },
                              { id: 'afternoon', start: BREAKS[1].end, end: BREAKS[2].start }, // After Lunch, Before Break
                              { id: 'break', type: 'break', break: BREAKS[2] },
                              { id: 'late-afternoon', start: BREAKS[2].end, end: SCHOOL_DAY_END }, // After Break
                            ];
                            
                            return sections.map((section) => {
                              if (section.type === 'break') {
                                const brk = section.break;
                                const timeRange = `${formatTime(brk.start)} - ${formatTime(brk.end)}`;
                                return (
                                  <div key={`break-${brk.id}`} className="flex items-center gap-3 py-3 px-4 rounded-xl bg-amber-50/50 border border-amber-100/60">
                                    <div className="w-9 h-9 rounded-full bg-amber-100 flex items-center justify-center text-lg flex-shrink-0">
                                      {brk.icon}
                                    </div>
                                    <div className="flex-1">
                                      <span className="font-medium text-amber-600 text-sm">{brk.name}</span>
                                      <p className="text-xs text-amber-400">{timeRange}</p>
                                    </div>
                                  </div>
                                );
                              }
                              
                              // Get lessons for each student in this time section
                              const sectionLessons = studentSchedules.map(ss => 
                                ss.schedule.filter(item => 
                                  item.type === 'lesson' && 
                                  item.start >= section.start && 
                                  item.start < section.end
                                )
                              );
                              
                              // Skip empty sections
                              if (sectionLessons.every(lessons => lessons.length === 0)) {
                                return null;
                              }
                              
                              return (
                                <div key={section.id} className="flex gap-3">
                                  {studentSchedules.map((ss, sIdx) => {
                                    const avatarColor = getStudentColor(sIdx);
                                    const lessons = sectionLessons[sIdx];
                                    
                                    return (
                                      <div key={ss.student.id} className="flex-1 space-y-1.5">
                                        {lessons.map((lesson) => {
                                          const colors = subjectColors[lesson.item.subject] || subjectColors['Other'];
                                          const isActive = activeSessions.some(s => s.work_item_id === lesson.item.id);
                                          const timeRange = `${formatTime(lesson.start)} - ${formatTime(lesson.end)}`;
                                          
                                          return (
                                            <div 
                                              key={`${ss.student.id}-${lesson.item.id}-${lesson.start}`}
                                              data-testid={`today-item-${lesson.item.id}`}
                                              className={`py-2.5 px-3 rounded-xl cursor-pointer transition-all ${
                                                isActive ? 'bg-green-50 border border-green-200' : `${colors.bg} hover:opacity-80`
                                              }`}
                                              onClick={() => navigate(`/work-items/${lesson.item.id}`)}
                                            >
                                              <div className="flex items-center gap-1.5">
                                                <span className="text-sm">{colors.icon}</span>
                                                <span className={`font-semibold text-sm ${isActive ? 'text-gray-800' : colors.text}`}>
                                                  {lesson.item.subject}
                                                </span>
                                                {isActive && (
                                                  <span className="px-1.5 py-0.5 bg-green-500 text-white text-[9px] rounded-full animate-pulse font-medium">LIVE</span>
                                                )}
                                              </div>
                                              <p className="text-xs text-gray-400 mt-0.5">{timeRange}</p>
                                            </div>
                                          );
                                        })}
                                      </div>
                                    );
                                  })}
                                </div>
                              );
                            });
                          })()}
                        </div>
                      ) : (
                        // Single student - use simpler layout
                        <div className="space-y-1">
                          {studentSchedules[0]?.schedule.map((schedItem, idx) => {
                            const timeRange = `${formatTime(schedItem.start)} - ${formatTime(schedItem.end)}`;
                            
                            if (schedItem.type === 'break') {
                              return (
                                <div key={`break-${schedItem.id}`} className="flex items-start gap-3 py-2.5 px-3 rounded-xl bg-amber-50/60 border border-amber-100/80">
                                  <div className="w-10 h-10 rounded-full bg-amber-100 flex items-center justify-center text-xl flex-shrink-0">
                                    {schedItem.icon}
                                  </div>
                                  <div className="flex-1 min-w-0">
                                    <span className="font-medium text-amber-700">{schedItem.name}</span>
                                    <p className="text-sm text-amber-500 mt-0.5">{timeRange}</p>
                                  </div>
                                </div>
                              );
                            }
                            
                            const { item } = schedItem;
                            const student = students.find(s => s.id === item.student_id);
                            const studentIdx = getStudentIndex(item.student_id);
                            const colors = subjectColors[item.subject] || subjectColors['Other'];
                            const avatarColor = getStudentColor(studentIdx);
                            const isActive = activeSessions.some(s => s.work_item_id === item.id);
                            
                            return (
                              <div 
                                key={`lesson-${item.id}-${schedItem.start}`}
                                data-testid={`today-item-${item.id}`}
                                className={`flex items-start gap-3 py-3 px-3 rounded-xl cursor-pointer transition-all ${
                                  isActive ? 'bg-green-50 border border-green-200' : 'hover:bg-gray-50'
                                }`}
                                onClick={() => navigate(`/work-items/${item.id}`)}
                              >
                                <div className={`w-10 h-10 rounded-full ${avatarColor.bg} ${avatarColor.text} flex items-center justify-center font-semibold text-sm shadow-sm flex-shrink-0`}>
                                  {student?.first_name?.[0] || '?'}
                                </div>
                                <div className="flex-1 min-w-0">
                                  <div className="flex items-center gap-2">
                                    <span className="text-base">{colors.icon}</span>
                                    <span className="font-semibold text-gray-800">
                                      {student?.first_name} — {item.subject}
                                    </span>
                                    {isActive && (
                                      <span className="px-2 py-0.5 bg-green-500 text-white text-[10px] rounded-full animate-pulse font-medium">LIVE</span>
                                    )}
                                  </div>
                                  <p className="text-sm text-gray-400 mt-0.5">{timeRange}</p>
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      )}
                    </div>
                  );
                })()}
              </div>
            )}
          </div>
        </details>
      </div>
    )}

        {/* Daily Progress - Compact */}
        <div className="bg-white rounded-xl shadow-sm p-4 mb-4">
          <div className="flex justify-between items-center mb-2">
            <span className="text-sm font-medium text-gray-600">Today's Progress</span>
            <span className="text-sm font-semibold text-indigo-600">{Math.floor(todayMinutes / 60)}h {todayMinutes % 60}m</span>
          </div>
          <div className="h-2 bg-gray-100 rounded-full overflow-hidden">
            <div 
              className="h-full bg-indigo-500 rounded-full transition-all"
              style={{ width: `${Math.min((todayMinutes / 300) * 100, 100)}%` }}
            />
          </div>
          <p className="text-xs text-gray-400 mt-1">Daily goal: 5 hours</p>
        </div>

        {/* Weekly Overview - Compact */}
        <div className="bg-white rounded-xl shadow-sm p-4 mb-4">
          <h3 className="font-medium text-gray-700 text-sm mb-3">This Week</h3>
          <div className="flex justify-between items-end h-16">
            {weeklyData.map((d) => (
              <div key={d.date} className="flex flex-col items-center flex-1">
                <div className="w-full px-0.5">
                  <div
                    className={`w-full rounded-t transition-all ${d.date === today ? 'bg-indigo-500' : 'bg-indigo-200'}`}
                    style={{ height: `${Math.max((d.minutes / maxWeekMinutes) * 40, 4)}px` }}
                  />
                </div>
                <span className={`text-[10px] mt-1 ${d.date === today ? 'text-indigo-600 font-semibold' : 'text-gray-400'}`}>
                  {d.day}
                </span>
              </div>
            ))}
          </div>
        </div>

        {/* Your School Year Card - At Bottom */}
        <SchoolYearCard compliance={compliance} onSetup={() => navigate('/settings')} onViewReport={() => navigate('/reports-list')} />
      </main>

      <BottomNav />

      {showStartLesson && (
        <StartLessonModal
          students={students}
          templates={templates}
          onClose={() => setShowStartLesson(false)}
          onStart={handleStartLesson}
          preselectedStudentId={activeStudentId}
        />
      )}

      {/* Session View Modal */}
      {showSessionView && activeLocalSession && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-end sm:items-center justify-center z-50" onClick={() => {}}>
          <div 
            data-testid="session-view-modal"
            className="bg-white rounded-t-2xl sm:rounded-2xl shadow-xl w-full sm:max-w-md max-h-[90vh] overflow-y-auto"
            onClick={e => e.stopPropagation()}
          >
            {/* Session Header */}
            <div className={`${activeLocalSession.status === 'active' ? 'bg-green-600' : 'bg-indigo-600'} text-white px-4 py-4 rounded-t-2xl sm:rounded-t-2xl`}>
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <span className={`text-xs px-2 py-0.5 rounded-full ${activeLocalSession.status === 'active' ? 'bg-green-500' : 'bg-indigo-500'}`}>
                    {activeLocalSession.status === 'active' ? 'Active' : 'Saved'}
                  </span>
                </div>
                <button 
                  onClick={() => { setShowSessionView(false); setActiveLocalSession(null); }}
                  className="text-white/80 hover:text-white"
                >
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
              <h2 className="text-lg font-bold mt-2">Learning Session</h2>
              <p className="text-sm text-white/70">
                Started {new Date(activeLocalSession.startTime).toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' })}
              </p>
            </div>

            <div className="p-4 space-y-4">
              {/* Session Notes - Always visible */}
              <div>
                <label className="block text-sm font-semibold text-gray-800 mb-2">Session Notes</label>
                <textarea
                  data-testid="session-notes-input"
                  value={sessionNotes}
                  onChange={(e) => setSessionNotes(e.target.value)}
                  placeholder="What are you learning? Add notes, observations, or reflections..."
                  rows={4}
                  className="w-full border border-gray-200 rounded-xl px-3 py-2 text-sm focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                />
              </div>

              {/* Helper text */}
              <p className="text-xs text-gray-400 text-center">
                Add notes, time, or evidence now — or later.
              </p>

              {/* Collapsible Details */}
              <details className="border border-gray-200 rounded-xl">
                <summary className="px-4 py-3 cursor-pointer font-medium text-gray-700 flex items-center justify-between">
                  <span>Details</span>
                  <svg className="w-5 h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                  </svg>
                </summary>
                <div className="px-4 pb-4 pt-0 border-t text-sm space-y-2">
                  <div className="flex justify-between pt-3">
                    <span className="text-gray-500">Session ID</span>
                    <span className="text-gray-600 font-mono text-xs">{activeLocalSession.id.slice(-8)}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-500">Started</span>
                    <span className="text-gray-700">{new Date(activeLocalSession.startTime).toLocaleString()}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-500">Status</span>
                    <span className={`px-2 py-0.5 rounded-full text-xs ${activeLocalSession.status === 'active' ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-600'}`}>
                      {activeLocalSession.status}
                    </span>
                  </div>
                </div>
              </details>

              {/* Done button */}
              <button
                data-testid="session-done-btn"
                onClick={saveSession}
                className="w-full bg-indigo-600 hover:bg-indigo-700 text-white py-3 rounded-xl font-semibold"
              >
                Done
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Today Activity View Modal */}
      {showTodayActivity && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-end sm:items-center justify-center z-50" onClick={() => setShowTodayActivity(false)}>
          <div 
            data-testid="today-activity-modal"
            className="bg-white rounded-t-2xl sm:rounded-2xl shadow-xl w-full sm:max-w-md max-h-[90vh] overflow-y-auto"
            onClick={e => e.stopPropagation()}
          >
            {/* Header */}
            <div className="bg-indigo-600 text-white px-4 py-4 rounded-t-2xl sm:rounded-t-2xl">
              <div className="flex items-center justify-between">
                <h2 className="text-lg font-bold">Today's Activity</h2>
                <button 
                  onClick={() => setShowTodayActivity(false)}
                  className="text-white/80 hover:text-white"
                >
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
              <p className="text-sm text-indigo-200">{new Date().toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric' })}</p>
            </div>

            <div className="p-4">
              {getTodaySessions().length === 0 && workItems.filter(w => w.date === today).length === 0 ? (
                // Empty state
                <div className="text-center py-8">
                  <div className="w-12 h-12 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-3">
                    <svg className="w-6 h-6 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                    </svg>
                  </div>
                  <h3 className="font-semibold text-gray-800 mb-1">Nothing logged yet</h3>
                  <p className="text-sm text-gray-500 mb-4">When you log learning, it will appear here.</p>
                  <button
                    data-testid="start-session-empty-btn"
                    onClick={() => { setShowTodayActivity(false); startSession(); }}
                    className="bg-indigo-600 hover:bg-indigo-700 text-white px-6 py-2 rounded-xl font-semibold"
                  >
                    Start session
                  </button>
                </div>
              ) : (
                // Activity list
                <div className="space-y-2 mb-4">
                  {/* Local sessions */}
                  {getTodaySessions().map(session => (
                    <div 
                      key={session.id}
                      data-testid={`activity-item-${session.id}`}
                      className="p-3 bg-gray-50 rounded-xl cursor-pointer hover:bg-gray-100 transition-colors"
                      onClick={() => loadSession(session.id)}
                    >
                      <div className="flex items-center justify-between mb-1">
                        <span className="text-xs text-gray-500">
                          {new Date(session.startTime).toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' })}
                        </span>
                        <span className={`text-xs px-2 py-0.5 rounded-full ${session.status === 'active' ? 'bg-green-100 text-green-700' : 'bg-gray-200 text-gray-600'}`}>
                          {session.status}
                        </span>
                      </div>
                      <p className="text-sm text-gray-700 line-clamp-2">
                        {session.notes || 'No notes'}
                      </p>
                    </div>
                  ))}
                  
                  {/* Logged work items from backend */}
                  {workItems.filter(w => w.date === today).map(item => {
                    const student = students.find(s => s.id === item.student_id);
                    return (
                      <div 
                        key={item.id}
                        className="p-3 bg-indigo-50 rounded-xl cursor-pointer hover:bg-indigo-100 transition-colors"
                        onClick={() => navigate(`/work-items/${item.id}`)}
                      >
                        <div className="flex items-center justify-between mb-1">
                          <span className="text-xs text-indigo-500">{student?.first_name} • {item.subject}</span>
                          <span className="text-xs bg-indigo-200 text-indigo-700 px-2 py-0.5 rounded-full">
                            {item.duration_minutes}m
                          </span>
                        </div>
                        <p className="text-sm text-gray-700 font-medium truncate">{item.title}</p>
                      </div>
                    );
                  })}
                </div>
              )}

              {/* Start another session button */}
              {(getTodaySessions().length > 0 || workItems.filter(w => w.date === today).length > 0) && (
                <button
                  data-testid="start-another-session-btn"
                  onClick={() => { setShowTodayActivity(false); startSession(); }}
                  className="w-full border-2 border-indigo-200 text-indigo-600 py-3 rounded-xl font-semibold hover:bg-indigo-50"
                >
                  Start another session
                </button>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// Add Student Modal
const AddStudentModal = ({ onClose, onAdd }) => {
  const [formData, setFormData] = useState({
    first_name: '',
    last_name: '',
    grade_level: '2',
    jurisdiction: 'MT'
  });

  const handleSubmit = (e) => {
    e.preventDefault();
    onAdd(formData);
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-end sm:items-center justify-center z-50" onClick={onClose}>
      <div 
        data-testid="add-student-modal" 
        className="bg-white rounded-t-2xl sm:rounded-2xl shadow-xl p-6 w-full sm:max-w-md"
        onClick={e => e.stopPropagation()}
      >
        <h3 className="text-lg font-semibold mb-4">Add Student</h3>
        <form onSubmit={handleSubmit}>
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">First Name</label>
                <input
                  data-testid="student-first-name"
                  type="text"
                  required
                  value={formData.first_name}
                  onChange={(e) => setFormData({ ...formData, first_name: e.target.value })}
                  className="w-full border rounded-lg px-3 py-2 focus:ring-2 focus:ring-indigo-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Last Name</label>
                <input
                  data-testid="student-last-name"
                  type="text"
                  required
                  value={formData.last_name}
                  onChange={(e) => setFormData({ ...formData, last_name: e.target.value })}
                  className="w-full border rounded-lg px-3 py-2 focus:ring-2 focus:ring-indigo-500"
                />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Grade</label>
                <select
                  data-testid="student-grade"
                  value={formData.grade_level}
                  onChange={(e) => setFormData({ ...formData, grade_level: e.target.value })}
                  className="w-full border rounded-lg px-3 py-2"
                >
                  {['K', '1', '2', '3', '4', '5', '6', '7', '8', '9', '10', '11', '12'].map(g => (
                    <option key={g} value={g}>Grade {g}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">State</label>
                <input
                  type="text"
                  value={formData.jurisdiction}
                  onChange={(e) => setFormData({ ...formData, jurisdiction: e.target.value })}
                  className="w-full border rounded-lg px-3 py-2"
                />
              </div>
            </div>
          </div>
          <div className="flex gap-3 mt-6">
            <button type="button" onClick={onClose} className="flex-1 py-2 text-gray-600 hover:bg-gray-100 rounded-lg">
              Cancel
            </button>
            <button data-testid="submit-student-btn" type="submit" className="flex-1 py-2 bg-indigo-600 hover:bg-indigo-700 text-white rounded-lg">
              Add Student
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

// Journal Page (Chronological Learning Record)
const JournalPage = () => {
  const [students, setStudents] = useState([]);
  const [workItems, setWorkItems] = useState([]);
  const [evidenceCounts, setEvidenceCounts] = useState({});
  const [config, setConfig] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedStudent, setSelectedStudent] = useState(() => getActiveStudentId() || 'all');
  const [selectedSubject, setSelectedSubject] = useState('all');
  const [weekOffset, setWeekOffset] = useState(0);
  const navigate = useNavigate();

  // Weekly entries state (localStorage-based)
  const [weeklyEntries, setWeeklyEntries] = useState(() => {
    const saved = localStorage.getItem('homested_weekly_entries');
    return saved ? JSON.parse(saved) : {};
  });
  const [editingLookingAhead, setEditingLookingAhead] = useState(false);
  const [editingReflection, setEditingReflection] = useState(false);

  const getWeekStart = (offset = 0) => {
    const d = new Date();
    d.setDate(d.getDate() - d.getDay() + (offset * 7));
    return d;
  };

  const weekStart = getWeekStart(weekOffset);
  const weekDates = getWeekDates(weekStart);
  const weekKey = weekDates[0]; // Use first day of week as key

  // Get/set weekly entries
  const getLookingAhead = () => weeklyEntries[weekKey]?.lookingAhead || { focus: '', keepInMind: '' };
  const getReflection = () => weeklyEntries[weekKey]?.reflection || { workedWell: '', challenging: '', nextWeek: '' };

  const saveLookingAhead = (data) => {
    const updated = {
      ...weeklyEntries,
      [weekKey]: { ...weeklyEntries[weekKey], lookingAhead: data }
    };
    setWeeklyEntries(updated);
    localStorage.setItem('homested_weekly_entries', JSON.stringify(updated));
    setEditingLookingAhead(false);
  };

  const saveReflection = (data) => {
    const updated = {
      ...weeklyEntries,
      [weekKey]: { ...weeklyEntries[weekKey], reflection: data }
    };
    setWeeklyEntries(updated);
    localStorage.setItem('homested_weekly_entries', JSON.stringify(updated));
    setEditingReflection(false);
  };

  const hasLookingAheadContent = () => {
    const la = getLookingAhead();
    return la.focus?.trim() || la.keepInMind?.trim();
  };

  const hasReflectionContent = () => {
    const r = getReflection();
    return r.workedWell?.trim() || r.challenging?.trim() || r.nextWeek?.trim();
  };

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [studentsRes, workItemsRes, configRes, evidenceRes] = await Promise.all([
        axios.get(`${API_URL}/api/students`),
        axios.get(`${API_URL}/api/work-items`),
        axios.get(`${API_URL}/api/config`),
        axios.get(`${API_URL}/api/evidence`)
      ]);
      setStudents(studentsRes.data);
      setWorkItems(workItemsRes.data);
      setConfig(configRes.data);
      
      // Count evidence per work item
      const counts = {};
      evidenceRes.data.forEach(ev => {
        counts[ev.work_item_id] = (counts[ev.work_item_id] || 0) + 1;
      });
      setEvidenceCounts(counts);
    } catch (err) {
      console.error('Error:', err);
    } finally {
      setLoading(false);
    }
  };

  const filteredItems = workItems.filter(w => {
    if (selectedStudent !== 'all' && w.student_id !== selectedStudent) return false;
    if (selectedSubject !== 'all' && w.subject !== selectedSubject) return false;
    return weekDates.includes(w.date);
  });

  const groupedByDate = weekDates.reduce((acc, date) => {
    acc[date] = filteredItems.filter(w => w.date === date);
    return acc;
  }, {});

  // Looking Ahead Card Component
  const LookingAheadCard = () => {
    const [localData, setLocalData] = useState(getLookingAhead());
    
    if (!editingLookingAhead && !hasLookingAheadContent()) {
      return (
        <button
          onClick={() => setEditingLookingAhead(true)}
          className="w-full border-2 border-dashed border-emerald-200 rounded-xl p-4 text-left hover:border-emerald-300 hover:bg-emerald-50/50 transition-colors mb-4"
        >
          <div className="flex items-center gap-2 text-emerald-600">
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
            </svg>
            <span className="font-medium text-sm">Add Looking Ahead</span>
          </div>
          <p className="text-xs text-gray-400 mt-1">Optional intentions for this week</p>
        </button>
      );
    }

    return (
      <div className="bg-gradient-to-br from-emerald-50 to-teal-50 rounded-xl p-4 mb-4 border border-emerald-100">
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 bg-emerald-100 rounded-lg flex items-center justify-center">
              <svg className="w-4 h-4 text-emerald-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7l5 5m0 0l-5 5m5-5H6" />
              </svg>
            </div>
            <span className="font-semibold text-emerald-800 text-sm">Looking Ahead</span>
          </div>
          {!editingLookingAhead && (
            <button onClick={() => setEditingLookingAhead(true)} className="text-emerald-600 text-xs font-medium hover:underline">
              Edit
            </button>
          )}
        </div>

        {editingLookingAhead ? (
          <div className="space-y-3">
            <div>
              <label className="block text-xs text-emerald-700 mb-1">What are we hoping to focus on this week?</label>
              <textarea
                value={localData.focus}
                onChange={(e) => setLocalData({ ...localData, focus: e.target.value })}
                placeholder="e.g., Finish the science unit, more outdoor learning..."
                rows={2}
                className="w-full border border-emerald-200 rounded-lg px-3 py-2 text-sm bg-white focus:ring-2 focus:ring-emerald-400 focus:border-transparent"
              />
            </div>
            <div>
              <label className="block text-xs text-emerald-700 mb-1">Anything we want to keep in mind?</label>
              <textarea
                value={localData.keepInMind}
                onChange={(e) => setLocalData({ ...localData, keepInMind: e.target.value })}
                placeholder="e.g., Taking it easy on Tuesday, field trip Friday..."
                rows={2}
                className="w-full border border-emerald-200 rounded-lg px-3 py-2 text-sm bg-white focus:ring-2 focus:ring-emerald-400 focus:border-transparent"
              />
            </div>
            <div className="flex gap-2 justify-end">
              <button onClick={() => setEditingLookingAhead(false)} className="text-gray-500 text-sm px-3 py-1.5">Cancel</button>
              <button onClick={() => saveLookingAhead(localData)} className="bg-emerald-600 text-white text-sm px-4 py-1.5 rounded-lg">Save</button>
            </div>
          </div>
        ) : (
          <div className="space-y-2 text-sm">
            {getLookingAhead().focus && (
              <div>
                <p className="text-xs text-emerald-600 font-medium">Focus</p>
                <p className="text-gray-700">{getLookingAhead().focus}</p>
              </div>
            )}
            {getLookingAhead().keepInMind && (
              <div>
                <p className="text-xs text-emerald-600 font-medium">Keep in mind</p>
                <p className="text-gray-700">{getLookingAhead().keepInMind}</p>
              </div>
            )}
          </div>
        )}
      </div>
    );
  };

  // Weekly Reflection Card Component
  const WeeklyReflectionCard = () => {
    const [localData, setLocalData] = useState(getReflection());
    
    if (!editingReflection && !hasReflectionContent()) {
      return (
        <button
          onClick={() => setEditingReflection(true)}
          className="w-full border-2 border-dashed border-violet-200 rounded-xl p-4 text-left hover:border-violet-300 hover:bg-violet-50/50 transition-colors mt-4"
        >
          <div className="flex items-center gap-2 text-violet-600">
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
            </svg>
            <span className="font-medium text-sm">Add Weekly Reflection</span>
          </div>
          <p className="text-xs text-gray-400 mt-1">Optional thoughts on how the week went</p>
        </button>
      );
    }

    return (
      <div className="bg-gradient-to-br from-violet-50 to-purple-50 rounded-xl p-4 mt-4 border border-violet-100">
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 bg-violet-100 rounded-lg flex items-center justify-center">
              <svg className="w-4 h-4 text-violet-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" />
              </svg>
            </div>
            <span className="font-semibold text-violet-800 text-sm">Weekly Reflection</span>
          </div>
          {!editingReflection && (
            <button onClick={() => setEditingReflection(true)} className="text-violet-600 text-xs font-medium hover:underline">
              Edit
            </button>
          )}
        </div>

        {editingReflection ? (
          <div className="space-y-3">
            <div>
              <label className="block text-xs text-violet-700 mb-1">What worked well this week?</label>
              <textarea
                value={localData.workedWell}
                onChange={(e) => setLocalData({ ...localData, workedWell: e.target.value })}
                placeholder="e.g., Morning reading sessions, hands-on science..."
                rows={2}
                className="w-full border border-violet-200 rounded-lg px-3 py-2 text-sm bg-white focus:ring-2 focus:ring-violet-400 focus:border-transparent"
              />
            </div>
            <div>
              <label className="block text-xs text-violet-700 mb-1">What felt challenging or draining?</label>
              <textarea
                value={localData.challenging}
                onChange={(e) => setLocalData({ ...localData, challenging: e.target.value })}
                placeholder="e.g., Staying focused after lunch, math worksheets..."
                rows={2}
                className="w-full border border-violet-200 rounded-lg px-3 py-2 text-sm bg-white focus:ring-2 focus:ring-violet-400 focus:border-transparent"
              />
            </div>
            <div>
              <label className="block text-xs text-violet-700 mb-1">Anything you want to keep in mind for next week?</label>
              <textarea
                value={localData.nextWeek}
                onChange={(e) => setLocalData({ ...localData, nextWeek: e.target.value })}
                placeholder="e.g., More breaks, try outdoor math..."
                rows={2}
                className="w-full border border-violet-200 rounded-lg px-3 py-2 text-sm bg-white focus:ring-2 focus:ring-violet-400 focus:border-transparent"
              />
            </div>
            <div className="flex gap-2 justify-end">
              <button onClick={() => setEditingReflection(false)} className="text-gray-500 text-sm px-3 py-1.5">Cancel</button>
              <button onClick={() => saveReflection(localData)} className="bg-violet-600 text-white text-sm px-4 py-1.5 rounded-lg">Save</button>
            </div>
          </div>
        ) : (
          <div className="space-y-2 text-sm">
            {getReflection().workedWell && (
              <div>
                <p className="text-xs text-violet-600 font-medium">Worked well</p>
                <p className="text-gray-700">{getReflection().workedWell}</p>
              </div>
            )}
            {getReflection().challenging && (
              <div>
                <p className="text-xs text-violet-600 font-medium">Challenging</p>
                <p className="text-gray-700">{getReflection().challenging}</p>
              </div>
            )}
            {getReflection().nextWeek && (
              <div>
                <p className="text-xs text-violet-600 font-medium">For next week</p>
                <p className="text-gray-700">{getReflection().nextWeek}</p>
              </div>
            )}
          </div>
        )}
      </div>
    );
  };

  if (loading) return <div className="min-h-screen bg-gray-50 flex items-center justify-center">Loading...</div>;

  return (
    <div data-testid="journal-page" className="min-h-screen bg-gray-50 pb-20">
      <header className="bg-white border-b px-4 py-3 sticky top-0 z-10">
        <h1 className="text-xl font-bold text-gray-800">Learning Journal</h1>
        <AlertBanner config={config} compact />
      </header>

      <main className="px-4 py-4">
        {/* Filters */}
        <div className="flex gap-2 mb-4 overflow-x-auto pb-2">
          <select
            value={selectedStudent}
            onChange={(e) => setSelectedStudent(e.target.value)}
            className="border rounded-lg px-3 py-2 text-sm bg-white"
          >
            <option value="all">All Students</option>
            {students.map(s => (
              <option key={s.id} value={s.id}>{s.first_name}</option>
            ))}
          </select>
          <select
            value={selectedSubject}
            onChange={(e) => setSelectedSubject(e.target.value)}
            className="border rounded-lg px-3 py-2 text-sm bg-white"
          >
            <option value="all">All Subjects</option>
            <option value="ELA">ELA</option>
            <option value="Math">Math</option>
            <option value="Science">Science</option>
            <option value="Social Studies">Social Studies</option>
            <option value="Other">Other</option>
          </select>
        </div>

        {/* Week Navigation */}
        <div className="flex items-center justify-between mb-4">
          <button onClick={() => setWeekOffset(o => o - 1)} className="p-2 hover:bg-gray-100 rounded-lg">
            <svg className="w-5 h-5 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
          </button>
          <span className="font-medium text-gray-700">
            {weekStart.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })} - {new Date(weekDates[6] + 'T00:00:00').toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
          </span>
          <button onClick={() => setWeekOffset(o => o + 1)} className="p-2 hover:bg-gray-100 rounded-lg" disabled={weekOffset >= 0}>
            <svg className={`w-5 h-5 ${weekOffset >= 0 ? 'text-gray-300' : 'text-gray-600'}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
            </svg>
          </button>
        </div>

        {/* Looking Ahead - Top of Week */}
        <LookingAheadCard />

        {/* Daily Breakdown */}
        <div className="space-y-3">
          {weekDates.map((date, dateIndex) => {
            const dayItems = groupedByDate[date];
            const dayMinutes = dayItems.reduce((sum, w) => sum + w.duration_minutes, 0);
            const isToday = date === new Date().toISOString().split('T')[0];
            const hasEntries = dayItems.length > 0;
            
            // For empty days, show minimal muted header only
            if (!hasEntries) {
              // Show "No entries recorded" only once per week (on the first empty day)
              const isFirstEmptyDay = weekDates.slice(0, dateIndex).every(d => groupedByDate[d].length > 0);
              
              return (
                <div key={date} className="px-3 py-2">
                  <span className="text-sm text-gray-400">
                    {new Date(date + 'T00:00:00').toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' })}
                  </span>
                  {isFirstEmptyDay && (
                    <span className="text-xs text-gray-300 ml-2">— No entries recorded</span>
                  )}
                </div>
              );
            }
            
            return (
              <div key={date} className={`bg-white rounded-xl shadow-sm overflow-hidden ${isToday ? 'ring-2 ring-indigo-500' : ''}`}>
                <div className="flex items-center justify-between px-4 py-3 bg-gray-50 border-b">
                  <div className="flex items-center gap-2">
                    <span className={`font-medium ${isToday ? 'text-indigo-600' : 'text-gray-700'}`}>
                      {new Date(date + 'T00:00:00').toLocaleDateString('en-US', { weekday: 'long', month: 'short', day: 'numeric' })}
                    </span>
                    {isToday && <span className="text-xs bg-indigo-100 text-indigo-600 px-2 py-0.5 rounded-full">Today</span>}
                  </div>
                  <span className="text-sm text-gray-500">{dayMinutes}m</span>
                </div>
                
                <div className="divide-y">
                  {dayItems.map(item => {
                    const student = students.find(s => s.id === item.student_id);
                    const colors = subjectColors[item.subject] || subjectColors['Other'];
                    return (
                      <div
                        key={item.id}
                        onClick={() => navigate(`/work-items/${item.id}`)}
                        className="flex items-center gap-3 px-4 py-3 hover:bg-gray-50 cursor-pointer"
                      >
                        <div className={`w-2 h-8 rounded-full ${colors.dot}`} />
                        <div className="flex-1 min-w-0">
                          <p className="font-medium text-gray-800 truncate">{student?.first_name}: {item.title}</p>
                          <p className="text-xs text-gray-500">{item.subject} • {item.duration_minutes}m</p>
                        </div>
                        <div className="flex items-center gap-1.5">
                          {evidenceCounts[item.id] > 0 && (
                            <span className="text-xs bg-amber-100 text-amber-700 px-2 py-0.5 rounded-full flex items-center gap-1">
                              <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.172 7l-6.586 6.586a2 2 0 102.828 2.828l6.414-6.586a4 4 0 00-5.656-5.656l-6.415 6.585a6 6 0 108.486 8.486L20.5 13" />
                              </svg>
                              {evidenceCounts[item.id]}
                            </span>
                          )}
                          {item.standard_ids?.length > 0 && (
                            <span className="text-xs bg-green-100 text-green-700 px-2 py-0.5 rounded-full">
                              {item.standard_ids.length} std
                            </span>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            );
          })}
        </div>

        {/* Weekly Reflection - Bottom of Week */}
        <WeeklyReflectionCard />
      </main>

      <BottomNav />
    </div>
  );
};

// Reports List Page
const ReportsListPage = () => {
  const [students, setStudents] = useState([]);
  const [config, setConfig] = useState(null);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    fetchData();
  }, []);

  // Auto-navigate to active student's report if one is selected
  useEffect(() => {
    if (!loading && students.length > 0) {
      const activeId = getActiveStudentId();
      if (activeId && students.find(s => s.id === activeId)) {
        navigate(`/reports/${activeId}`, { replace: true });
      }
    }
  }, [loading, students, navigate]);

  const fetchData = async () => {
    try {
      const [studentsRes, configRes] = await Promise.all([
        axios.get(`${API_URL}/api/students`),
        axios.get(`${API_URL}/api/config`)
      ]);
      setStudents(studentsRes.data);
      setConfig(configRes.data);
    } catch (err) {
      console.error('Error:', err);
    } finally {
      setLoading(false);
    }
  };

  if (loading) return <div className="min-h-screen bg-gray-50 flex items-center justify-center">Loading...</div>;

  return (
    <div data-testid="reports-list-page" className="min-h-screen bg-gray-50 pb-20">
      <header className="bg-white border-b px-4 py-3">
        <h1 className="text-xl font-bold text-gray-800">Reports</h1>
      </header>

      <main className="px-4 py-4">
        <AlertBanner config={config} />

        {students.length === 0 ? (
          <div className="bg-white rounded-xl shadow-sm p-8 text-center">
            <p className="text-gray-500">Add students to generate reports</p>
          </div>
        ) : (
          <div className="space-y-3">
            {students.map(s => (
              <div
                key={s.id}
                onClick={() => navigate(`/reports/${s.id}`)}
                className="bg-white rounded-xl shadow-sm p-4 hover:shadow-md cursor-pointer transition-shadow"
              >
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 rounded-full bg-indigo-100 flex items-center justify-center text-indigo-600 font-semibold">
                    {s.first_name[0]}{s.last_name[0]}
                  </div>
                  <div className="flex-1">
                    <p className="font-semibold text-gray-800">{s.first_name} {s.last_name}</p>
                    <p className="text-sm text-gray-500">Grade {s.grade_level} • {s.jurisdiction}</p>
                  </div>
                  <svg className="w-5 h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                  </svg>
                </div>
              </div>
            ))}
          </div>
        )}
      </main>

      <BottomNav />
    </div>
  );
};

// Student Detail Page
const StudentDetailPage = () => {
  const { studentId } = useParams();
  const navigate = useNavigate();
  const [student, setStudent] = useState(null);
  const [workItems, setWorkItems] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [config, setConfig] = useState(null);
  const [showQuickAdd, setShowQuickAdd] = useState(false);
  const [showStartLesson, setShowStartLesson] = useState(false);
  const [activeSession, setActiveSession] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetchData = useCallback(async () => {
    try {
      const [studentRes, workItemsRes, configRes, templatesRes, sessionRes] = await Promise.all([
        axios.get(`${API_URL}/api/students/${studentId}`),
        axios.get(`${API_URL}/api/work-items?student_id=${studentId}`),
        axios.get(`${API_URL}/api/config`),
        axios.get(`${API_URL}/api/templates`),
        axios.get(`${API_URL}/api/active-sessions/student/${studentId}`)
      ]);
      setStudent(studentRes.data);
      setWorkItems(workItemsRes.data);
      setConfig(configRes.data);
      setTemplates(templatesRes.data);
      setActiveSession(sessionRes.data);
    } catch (err) {
      console.error('Error:', err);
    } finally {
      setLoading(false);
    }
  }, [studentId]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleQuickAdd = async (data) => {
    try {
      await axios.post(`${API_URL}/api/work-items`, data);
      setShowQuickAdd(false);
      fetchData();
    } catch (err) {
      console.error('Error:', err);
    }
  };

  const handleStartLesson = (result) => {
    setShowStartLesson(false);
    if (result.work_item) {
      navigate(`/work-items/${result.work_item.id}`);
    }
    fetchData();
  };

  const handleStopSession = async (sessionId, elapsedMinutes) => {
    try {
      await axios.post(`${API_URL}/api/active-sessions/stop-and-save/${sessionId}?elapsed_minutes=${elapsedMinutes}`);
      fetchData();
    } catch (err) {
      console.error('Error stopping session:', err);
    }
  };

  const handleDelete = async () => {
    if (!window.confirm('Delete this student and all their activities?')) return;
    try {
      await axios.delete(`${API_URL}/api/students/${studentId}`);
      navigate('/');
    } catch (err) {
      console.error('Error:', err);
    }
  };

  if (loading) return <div className="min-h-screen bg-gray-50 flex items-center justify-center">Loading...</div>;
  if (!student) return <div className="min-h-screen bg-gray-50 flex items-center justify-center">Not found</div>;

  const totalMinutes = workItems.reduce((sum, w) => sum + w.duration_minutes, 0);
  const gradeWarning = student.grade_level !== '2';

  return (
    <div data-testid="student-detail-page" className="min-h-screen bg-gray-50 pb-20">
      <header className="bg-indigo-600 text-white px-4 py-4">
        <div className="flex items-center gap-3">
          <button onClick={() => navigate('/')} className="p-1">
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
          </button>
          <div className="flex-1">
            <h1 className="text-xl font-bold">{student.first_name} {student.last_name}</h1>
            <p className="text-indigo-200 text-sm">Grade {student.grade_level} • {student.jurisdiction}</p>
          </div>
          <button
            onClick={() => navigate(`/reports/${studentId}`)}
            className="bg-indigo-500 px-3 py-1.5 rounded-lg text-sm"
          >
            Report
          </button>
        </div>
      </header>

      {/* Active Lesson Banner */}
      {activeSession && (
        <ActiveLessonBanner
          session={activeSession}
          student={student}
          onNavigate={(workItemId) => navigate(`/work-items/${workItemId}`)}
          onStop={handleStopSession}
        />
      )}

      <main className="px-4 py-4">
        <AlertBanner config={config} />

        {gradeWarning && (
          <div data-testid="grade-warning" className="bg-orange-50 border-l-4 border-orange-400 text-orange-800 p-3 mb-4 rounded-r text-sm">
            ⚠️ Standards library only includes Grade 2 ELA
          </div>
        )}

        {/* Start Lesson Button */}
        {!activeSession && (
          <button
            data-testid="start-lesson-student-btn"
            onClick={() => setShowStartLesson(true)}
            className="w-full bg-green-500 hover:bg-green-600 text-white py-4 rounded-xl font-semibold mb-4 flex items-center justify-center gap-2 shadow-lg"
          >
            <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM9.555 7.168A1 1 0 008 8v4a1 1 0 001.555.832l3-2a1 1 0 000-1.664l-3-2z" clipRule="evenodd" />
            </svg>
            Start Lesson
          </button>
        )}

        {/* Stats */}
        <div className="grid grid-cols-3 gap-3 mb-4">
          <div className="bg-white rounded-xl shadow-sm p-3 text-center">
            <p className="text-2xl font-bold text-indigo-600">{Math.floor(totalMinutes / 60)}</p>
            <p className="text-xs text-gray-500">Hours</p>
          </div>
          <div className="bg-white rounded-xl shadow-sm p-3 text-center">
            <p className="text-2xl font-bold text-gray-800">{workItems.length}</p>
            <p className="text-xs text-gray-500">Activities</p>
          </div>
          <div className="bg-white rounded-xl shadow-sm p-3 text-center">
            <p className="text-2xl font-bold text-green-600">
              {new Set(workItems.flatMap(w => w.standard_ids || [])).size}
            </p>
            <p className="text-xs text-gray-500">Standards</p>
          </div>
        </div>

        {/* Quick Add */}
        {showQuickAdd ? (
          <QuickAddWorkItem
            studentId={studentId}
            studentName={student.first_name}
            onAdd={handleQuickAdd}
            onCancel={() => setShowQuickAdd(false)}
          />
        ) : (
          <button
            data-testid="add-work-item-btn"
            onClick={() => setShowQuickAdd(true)}
            className="w-full bg-indigo-600 hover:bg-indigo-700 text-white py-3 rounded-xl font-medium mb-4 flex items-center justify-center gap-2"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
            </svg>
            Add Activity
          </button>
        )}

        {/* Work Items */}
        <div className="bg-white rounded-xl shadow-sm overflow-hidden">
          <div className="px-4 py-3 border-b bg-gray-50">
            <h2 className="font-semibold text-gray-800">Activities</h2>
          </div>
          {workItems.length === 0 ? (
            <p className="text-gray-400 text-sm py-8 text-center">No activities yet</p>
          ) : (
            <div className="divide-y">
              {workItems.map(item => {
                const colors = subjectColors[item.subject] || subjectColors['Other'];
                return (
                  <div
                    key={item.id}
                    data-testid={`work-item-${item.id}`}
                    onClick={() => navigate(`/work-items/${item.id}`)}
                    className="flex items-center gap-3 px-4 py-3 hover:bg-gray-50 cursor-pointer"
                  >
                    <div className={`w-2 h-10 rounded-full ${colors.dot}`} />
                    <div className="flex-1 min-w-0">
                      <p className="font-medium text-gray-800 truncate">{item.title}</p>
                      <p className="text-xs text-gray-500">{formatDate(item.date)} • {item.subject}</p>
                    </div>
                    <div className="text-right">
                      <span className="text-sm font-medium text-gray-700">{item.duration_minutes}m</span>
                      {item.standard_ids?.length > 0 && (
                        <p className="text-xs text-green-600">{item.standard_ids.length} std</p>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        <button
          onClick={handleDelete}
          className="w-full mt-4 py-2 text-red-600 text-sm hover:bg-red-50 rounded-lg"
        >
          Delete Student
        </button>
      </main>

      <BottomNav />

      {showStartLesson && (
        <StartLessonModal
          students={[student]}
          templates={templates}
          preselectedStudentId={studentId}
          onClose={() => setShowStartLesson(false)}
          onStart={handleStartLesson}
        />
      )}
    </div>
  );
};

// Work Item Detail Page (with Timer)
const WorkItemDetailPage = () => {
  const { workItemId } = useParams();
  const navigate = useNavigate();
  const [workItem, setWorkItem] = useState(null);
  const [student, setStudent] = useState(null);
  const [linkedStandards, setLinkedStandards] = useState([]);
  const [evidence, setEvidence] = useState([]);
  const [config, setConfig] = useState(null);
  const [showStandardsPicker, setShowStandardsPicker] = useState(false);
  const [showAddEvidence, setShowAddEvidence] = useState(false);
  const [editing, setEditing] = useState(false);
  const [editData, setEditData] = useState({});
  const [timerRunning, setTimerRunning] = useState(false);
  const [timerSeconds, setTimerSeconds] = useState(0);
  const [loading, setLoading] = useState(true);

  const fetchData = useCallback(async () => {
    try {
      const [workItemRes, standardsRes, evidenceRes, configRes] = await Promise.all([
        axios.get(`${API_URL}/api/work-items/${workItemId}`),
        axios.get(`${API_URL}/api/work-items/${workItemId}/standards`),
        axios.get(`${API_URL}/api/evidence?work_item_id=${workItemId}`),
        axios.get(`${API_URL}/api/config`)
      ]);
      setWorkItem(workItemRes.data);
      setLinkedStandards(standardsRes.data);
      setEvidence(evidenceRes.data);
      setConfig(configRes.data);
      setEditData(workItemRes.data);

      const studentRes = await axios.get(`${API_URL}/api/students/${workItemRes.data.student_id}`);
      setStudent(studentRes.data);
    } catch (err) {
      console.error('Error:', err);
    } finally {
      setLoading(false);
    }
  }, [workItemId]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleUpdate = async () => {
    try {
      await axios.put(`${API_URL}/api/work-items/${workItemId}`, {
        title: editData.title,
        date: editData.date,
        duration_minutes: editData.duration_minutes,
        notes: editData.notes,
        subject: editData.subject
      });
      setEditing(false);
      fetchData();
    } catch (err) {
      console.error('Error:', err);
    }
  };

  const handleTimerStop = async (minutes) => {
    setTimerRunning(false);
    const newDuration = workItem.duration_minutes + minutes;
    try {
      await axios.put(`${API_URL}/api/work-items/${workItemId}`, { duration_minutes: newDuration });
      fetchData();
      setTimerSeconds(0);
    } catch (err) {
      console.error('Error:', err);
    }
  };

  const handleRemoveStandard = async (standardId) => {
    try {
      await axios.delete(`${API_URL}/api/work-items/${workItemId}/standards/${standardId}`);
      fetchData();
    } catch (err) {
      console.error('Error:', err);
    }
  };

  const handleAddEvidence = async (data) => {
    try {
      await axios.post(`${API_URL}/api/evidence`, { ...data, work_item_id: workItemId });
      setShowAddEvidence(false);
      fetchData();
    } catch (err) {
      console.error('Error:', err);
    }
  };

  const handleDelete = async () => {
    if (!window.confirm('Delete this activity?')) return;
    try {
      await axios.delete(`${API_URL}/api/work-items/${workItemId}`);
      navigate(-1);
    } catch (err) {
      console.error('Error:', err);
    }
  };

  if (loading) return <div className="min-h-screen bg-gray-50 flex items-center justify-center">Loading...</div>;
  if (!workItem) return <div className="min-h-screen bg-gray-50 flex items-center justify-center">Not found</div>;

  const colors = subjectColors[workItem.subject] || subjectColors['Other'];
  const isActive = timerRunning;
  const sessionStatus = isActive ? 'Active' : 'Completed';

  return (
    <div data-testid="work-item-detail-page" className="min-h-screen bg-gray-50 pb-20">
      <header className={`${isActive ? 'bg-green-600' : 'bg-indigo-600'} text-white px-4 py-4 transition-colors`}>
        <div className="flex items-center gap-3">
          <button onClick={() => navigate(-1)} className="p-1">
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
          </button>
          <div className="flex-1">
            <div className="flex items-center gap-2">
              <span className={`text-xs px-2 py-0.5 rounded-full ${isActive ? 'bg-green-500 text-white' : 'bg-indigo-500 text-indigo-100'}`}>
                {sessionStatus}
              </span>
            </div>
            <h1 className="text-lg font-bold truncate mt-1">{workItem.title || 'Lesson Session'}</h1>
          </div>
        </div>
      </header>

      <main className="px-4 py-4">
        <AlertBanner config={config} />

        {/* Lesson Focus Block */}
        <div className={`${colors.bg} rounded-xl p-4 mb-4 border-l-4 ${colors.dot.replace('bg-', 'border-')}`}>
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600 mb-1">Session for</p>
              <p className="font-semibold text-gray-800">{student?.first_name} {student?.last_name}</p>
            </div>
            <div className="text-right">
              <span className={`inline-block px-3 py-1 rounded-full text-sm font-medium ${colors.bg} ${colors.text}`}>
                {workItem.subject}
              </span>
              {linkedStandards.length > 0 && (
                <p className="text-xs text-gray-500 mt-1">{linkedStandards.length} standards linked</p>
              )}
            </div>
          </div>
        </div>

        {/* Timer Card */}
        <div className={`bg-white rounded-xl shadow-sm p-4 mb-4 ${isActive ? 'ring-2 ring-green-500' : ''}`}>
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-500">Logged Time</p>
              <p className="text-2xl font-bold text-gray-800">{workItem.duration_minutes} min</p>
            </div>
            <div className="text-right">
              <p className="text-xs text-gray-400 mb-1">{isActive ? 'Recording...' : 'Add more time'}</p>
              <Timer
                initialSeconds={timerSeconds}
                onTimeUpdate={setTimerSeconds}
                isRunning={timerRunning}
                onToggle={() => setTimerRunning(!timerRunning)}
                onStop={handleTimerStop}
              />
            </div>
          </div>
        </div>

        {/* Session Notes - Always visible */}
        <div className="bg-white rounded-xl shadow-sm p-4 mb-4">
          <div className="flex justify-between items-center mb-2">
            <h2 className="font-semibold text-gray-800">Session Notes</h2>
            {!editing && (
              <button onClick={() => setEditing(true)} className="text-indigo-600 text-sm font-medium">Edit</button>
            )}
          </div>
          {editing ? (
            <div>
              <textarea
                data-testid="session-notes-input"
                value={editData.notes || ''}
                onChange={(e) => setEditData({ ...editData, notes: e.target.value })}
                placeholder="Add notes about this session..."
                rows={4}
                className="w-full border rounded-lg px-3 py-2 text-sm"
              />
              <div className="flex justify-end gap-2 mt-2">
                <button onClick={() => { setEditing(false); setEditData(workItem); }} className="text-gray-500 text-sm px-3 py-1">Cancel</button>
                <button data-testid="save-notes-btn" onClick={handleUpdate} className="text-white bg-indigo-600 text-sm px-3 py-1 rounded-lg">Save</button>
              </div>
            </div>
          ) : (
            <p className={`text-sm ${workItem.notes ? 'text-gray-700' : 'text-gray-400 italic'}`}>
              {workItem.notes || 'No notes yet. Tap Edit to add.'}
            </p>
          )}
        </div>

        {/* Details (collapsed by default) */}
        <details className="bg-white rounded-xl shadow-sm mb-4">
          <summary className="p-4 cursor-pointer font-semibold text-gray-800 flex items-center justify-between">
            <span>Details</span>
            <svg className="w-5 h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
            </svg>
          </summary>
          <div className="px-4 pb-4 pt-0 border-t">
            <div className="space-y-2 text-sm pt-3">
              <div className="flex justify-between">
                <span className="text-gray-500">Date</span>
                <span className="text-gray-800">{formatDate(workItem.date)}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-500">Subject</span>
                <span className={`px-2 py-0.5 rounded-full ${colors.bg} ${colors.text}`}>{workItem.subject}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-500">Duration</span>
                <span className="text-gray-800">{workItem.duration_minutes} min</span>
              </div>
            </div>
          </div>
        </details>

        {/* Standards */}
        <div className="bg-white rounded-xl shadow-sm p-4 mb-4">
          <div className="flex justify-between items-center mb-3">
            <h2 className="font-semibold text-gray-800">Standards</h2>
            <button
              data-testid="add-standards-btn"
              onClick={() => setShowStandardsPicker(true)}
              className="text-indigo-600 text-sm font-medium"
            >
              + Add
            </button>
          </div>
          {linkedStandards.length === 0 ? (
            <p className="text-gray-400 text-sm">No standards linked</p>
          ) : (
            <div className="space-y-2">
              {linkedStandards.map(std => (
                <div key={std.id} className="flex items-start gap-2 p-2 bg-gray-50 rounded-lg">
                  <span className="font-mono text-indigo-600 text-sm font-medium">{std.code}</span>
                  <p className="flex-1 text-xs text-gray-600 line-clamp-2">{std.label}</p>
                  <button onClick={() => handleRemoveStandard(std.id)} className="text-gray-400 hover:text-red-500">
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Evidence */}
        <div className="bg-white rounded-xl shadow-sm p-4 mb-4">
          <div className="flex justify-between items-center mb-3">
            <h2 className="font-semibold text-gray-800">Evidence</h2>
            <button
              data-testid="add-evidence-btn"
              onClick={() => setShowAddEvidence(true)}
              className="text-indigo-600 text-sm font-medium"
            >
              + Add
            </button>
          </div>
          {evidence.length === 0 ? (
            <p className="text-gray-400 text-sm">No evidence attached</p>
          ) : (
            <div className="space-y-2">
              {evidence.map(ev => (
                <div key={ev.id} className="p-3 bg-gray-50 rounded-lg">
                  <div className="flex items-start gap-3">
                    {/* Icon based on type */}
                    <div className={`w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0 ${
                      ev.file_type === 'image' ? 'bg-green-100' : ev.link_url ? 'bg-blue-100' : 'bg-gray-200'
                    }`}>
                      {ev.file_type === 'image' ? (
                        <svg className="w-5 h-5 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                        </svg>
                      ) : ev.file_url ? (
                        <svg className="w-5 h-5 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                        </svg>
                      ) : (
                        <svg className="w-5 h-5 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101m-.758-4.899a4 4 0 005.656 0l4-4a4 4 0 00-5.656-5.656l-1.1 1.1" />
                        </svg>
                      )}
                    </div>
                    <div className="flex-1 min-w-0">
                      {ev.file_url ? (
                        <a 
                          href={`${API_URL}${ev.file_url}`} 
                          target="_blank" 
                          rel="noopener noreferrer" 
                          className="text-sm font-medium text-gray-800 hover:text-indigo-600 truncate block"
                        >
                          {ev.filename || 'View File'}
                        </a>
                      ) : ev.link_url ? (
                        <a 
                          href={ev.link_url} 
                          target="_blank" 
                          rel="noopener noreferrer" 
                          className="text-sm font-medium text-blue-600 hover:underline truncate block"
                        >
                          {ev.link_url}
                        </a>
                      ) : ev.file_reference ? (
                        <p className="text-sm font-medium text-gray-800">{ev.file_reference}</p>
                      ) : null}
                      {ev.description && <p className="text-xs text-gray-500 mt-0.5">{ev.description}</p>}
                      {ev.file_size && (
                        <p className="text-xs text-gray-400 mt-0.5">
                          {ev.file_size < 1024 * 1024 
                            ? (ev.file_size / 1024).toFixed(1) + ' KB'
                            : (ev.file_size / (1024 * 1024)).toFixed(1) + ' MB'
                          }
                        </p>
                      )}
                    </div>
                    <button 
                      onClick={async () => {
                        if (window.confirm('Remove this evidence?')) {
                          await axios.delete(`${API_URL}/api/evidence/${ev.id}`);
                          fetchData();
                        }
                      }}
                      className="text-gray-400 hover:text-red-500 p-1"
                    >
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                      </svg>
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        <button onClick={handleDelete} className="w-full py-2 text-red-600 text-sm hover:bg-red-50 rounded-lg">
          Delete Activity
        </button>
      </main>

      <BottomNav />

      {showStandardsPicker && (
        <StandardsPickerModal
          workItemId={workItemId}
          linkedStandardIds={linkedStandards.map(s => s.id)}
          onClose={() => setShowStandardsPicker(false)}
          onUpdate={fetchData}
          config={config}
        />
      )}

      {showAddEvidence && (
        <AddEvidenceModal 
          onClose={() => setShowAddEvidence(false)} 
          onAdd={handleAddEvidence} 
          workItemId={workItemId}
        />
      )}
    </div>
  );
};

// Standards Picker Modal
const StandardsPickerModal = ({ workItemId, linkedStandardIds: initialLinkedIds, onClose, onUpdate, config }) => {
  const [standards, setStandards] = useState([]);
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(true);
  const [linkedIds, setLinkedIds] = useState(initialLinkedIds || []);

  useEffect(() => {
    fetchStandards();
  }, [search]);

  const fetchStandards = async () => {
    try {
      const params = new URLSearchParams();
      params.append('jurisdiction', 'Montana');
      if (search) params.append('search', search);
      const res = await axios.get(`${API_URL}/api/standards?${params}`);
      setStandards(res.data);
    } catch (err) {
      console.error('Error:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleToggle = async (standardId, isLinked) => {
    if (isLinked) {
      setLinkedIds(prev => prev.filter(id => id !== standardId));
    } else {
      setLinkedIds(prev => [...prev, standardId]);
    }
    try {
      if (isLinked) {
        await axios.delete(`${API_URL}/api/work-items/${workItemId}/standards/${standardId}`);
      } else {
        await axios.post(`${API_URL}/api/work-items/${workItemId}/standards/${standardId}`);
      }
      onUpdate();
    } catch (err) {
      console.error('Error:', err);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-end sm:items-center justify-center z-50" onClick={onClose}>
      <div 
        data-testid="standards-picker-modal"
        className="bg-white rounded-t-2xl sm:rounded-2xl shadow-xl w-full sm:max-w-lg max-h-[80vh] flex flex-col"
        onClick={e => e.stopPropagation()}
      >
        <div className="p-4 border-b">
          <div className="flex justify-between items-center mb-3">
            <h3 className="text-lg font-semibold">Select Standards</h3>
            <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
          {config?.standards_is_partial && (
            <div className="bg-amber-50 text-amber-700 text-xs p-2 rounded mb-3">⚠️ {config.standards_scope_note}</div>
          )}
          <input
            data-testid="standards-search"
            type="text"
            placeholder="Search standards..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="w-full border rounded-lg px-3 py-2"
          />
        </div>

        <div className="flex-1 overflow-y-auto p-4">
          {loading ? (
            <p className="text-center text-gray-500">Loading...</p>
          ) : standards.length === 0 ? (
            <p className="text-center text-gray-500">No standards found</p>
          ) : (
            <div className="space-y-2">
              {standards.map(std => {
                const isLinked = linkedIds.includes(std.id);
                return (
                  <div
                    key={std.id}
                    data-testid={`standard-option-${std.id}`}
                    onClick={() => handleToggle(std.id, isLinked)}
                    className={`p-3 rounded-lg cursor-pointer transition-colors ${isLinked ? 'bg-indigo-100 border border-indigo-300' : 'bg-gray-50 hover:bg-gray-100'}`}
                  >
                    <div className="flex items-start gap-3">
                      <div className={`w-5 h-5 rounded border-2 flex items-center justify-center mt-0.5 ${isLinked ? 'bg-indigo-500 border-indigo-500' : 'border-gray-300'}`}>
                        {isLinked && <svg className="w-3 h-3 text-white" fill="currentColor" viewBox="0 0 20 20"><path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" /></svg>}
                      </div>
                      <div className="flex-1 min-w-0">
                        <span className="font-mono text-indigo-600 text-sm font-medium">{std.code}</span>
                        <p className="text-xs text-gray-600 line-clamp-2">{std.label}</p>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        <div className="p-4 border-t">
          <button onClick={onClose} className="w-full bg-indigo-600 hover:bg-indigo-700 text-white py-2 rounded-lg">
            Done
          </button>
        </div>
      </div>
    </div>
  );
};

// Add Evidence Modal
const AddEvidenceModal = ({ onClose, onAdd, workItemId }) => {
  const [mode, setMode] = useState('file'); // 'file' or 'link'
  const [linkUrl, setLinkUrl] = useState('');
  const [description, setDescription] = useState('');
  const [selectedFile, setSelectedFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState('');
  const fileInputRef = useRef(null);

  const handleFileSelect = (e) => {
    const file = e.target.files[0];
    if (file) {
      // Validate file type
      const allowedTypes = ['.jpg', '.jpeg', '.png', '.gif', '.pdf', '.doc', '.docx'];
      const ext = '.' + file.name.split('.').pop().toLowerCase();
      if (!allowedTypes.includes(ext)) {
        setError('File type not allowed. Use: JPG, PNG, GIF, PDF, DOC');
        return;
      }
      // Validate file size (10MB)
      if (file.size > 10 * 1024 * 1024) {
        setError('File too large. Maximum size is 10MB.');
        return;
      }
      setSelectedFile(file);
      setError('');
    }
  };

  const handleSubmit = async () => {
    setError('');
    setUploading(true);
    try {
      if (mode === 'file' && selectedFile) {
        // Upload file
        const formData = new FormData();
        formData.append('file', selectedFile);
        formData.append('work_item_id', workItemId);
        if (description) formData.append('description', description);
        
        await axios.post(`${API_URL}/api/evidence/upload`, formData, {
          headers: { 'Content-Type': 'multipart/form-data' }
        });
      } else if (mode === 'link' && linkUrl) {
        // Add link
        await onAdd({ link_url: linkUrl, description });
        return; // onAdd handles closing
      } else {
        setError('Please select a file or enter a link');
        setUploading(false);
        return;
      }
      onClose();
      window.location.reload(); // Refresh to show new evidence
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to add evidence');
    } finally {
      setUploading(false);
    }
  };

  const formatFileSize = (bytes) => {
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
    return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-end sm:items-center justify-center z-50" onClick={onClose}>
      <div className="bg-white rounded-t-2xl sm:rounded-2xl shadow-xl p-6 w-full sm:max-w-md" onClick={e => e.stopPropagation()}>
        <h3 className="text-lg font-semibold mb-4">Add Evidence</h3>
        
        {/* Mode Toggle */}
        <div className="flex gap-2 mb-4">
          <button
            onClick={() => setMode('file')}
            className={`flex-1 py-2 px-3 rounded-lg text-sm font-medium transition-colors ${
              mode === 'file' 
                ? 'bg-indigo-600 text-white' 
                : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
            }`}
          >
            📎 Upload File
          </button>
          <button
            onClick={() => setMode('link')}
            className={`flex-1 py-2 px-3 rounded-lg text-sm font-medium transition-colors ${
              mode === 'link' 
                ? 'bg-indigo-600 text-white' 
                : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
            }`}
          >
            🔗 Add Link
          </button>
        </div>

        {error && (
          <div className="mb-3 p-2 bg-red-50 border border-red-200 rounded-lg text-red-600 text-sm">
            {error}
          </div>
        )}

        {mode === 'file' ? (
          <div className="space-y-3">
            <input
              type="file"
              ref={fileInputRef}
              onChange={handleFileSelect}
              className="hidden"
              accept=".jpg,.jpeg,.png,.gif,.pdf,.doc,.docx"
            />
            <button
              onClick={() => fileInputRef.current?.click()}
              className="w-full border-2 border-dashed border-gray-300 rounded-lg p-6 text-center hover:border-indigo-400 transition-colors"
            >
              {selectedFile ? (
                <div>
                  <p className="font-medium text-gray-800">{selectedFile.name}</p>
                  <p className="text-sm text-gray-500">{formatFileSize(selectedFile.size)}</p>
                </div>
              ) : (
                <div>
                  <svg className="w-8 h-8 mx-auto text-gray-400 mb-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
                  </svg>
                  <p className="text-gray-600">Click to select file</p>
                  <p className="text-xs text-gray-400 mt-1">JPG, PNG, PDF, DOC • Max 10MB</p>
                </div>
              )}
            </button>
          </div>
        ) : (
          <div className="space-y-3">
            <input
              type="url"
              placeholder="https://..."
              value={linkUrl}
              onChange={(e) => setLinkUrl(e.target.value)}
              className="w-full border rounded-lg px-3 py-2"
            />
          </div>
        )}

        <textarea
          placeholder="Description (optional)"
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          rows={2}
          className="w-full border rounded-lg px-3 py-2 mt-3"
        />

        <div className="flex gap-3 mt-4">
          <button 
            onClick={onClose} 
            disabled={uploading}
            className="flex-1 py-2 text-gray-600 hover:bg-gray-100 rounded-lg disabled:opacity-50"
          >
            Cancel
          </button>
          <button 
            onClick={handleSubmit} 
            disabled={uploading || (mode === 'file' && !selectedFile) || (mode === 'link' && !linkUrl)}
            className="flex-1 py-2 bg-indigo-600 text-white rounded-lg disabled:opacity-50 flex items-center justify-center gap-2"
          >
            {uploading ? (
              <>
                <svg className="w-4 h-4 animate-spin" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
                Uploading...
              </>
            ) : (
              'Add'
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

// Report Page
// State Compliance Report Page (Stitch Design)
const ReportPage = () => {
  const { studentId } = useParams();
  const navigate = useNavigate();
  const [report, setReport] = useState(null);
  const [compliance, setCompliance] = useState(null);
  const [schoolYearSettings, setSchoolYearSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showActivities, setShowActivities] = useState(false);
  const [exporting, setExporting] = useState(false);

  const fetchReport = useCallback(async () => {
    try {
      const [reportRes, complianceRes, settingsRes] = await Promise.all([
        axios.get(`${API_URL}/api/reports/student/${studentId}`),
        axios.get(`${API_URL}/api/compliance/student/${studentId}`),
        axios.get(`${API_URL}/api/school-year-settings`)
      ]);
      setReport(reportRes.data);
      setCompliance(complianceRes.data);
      setSchoolYearSettings(settingsRes.data);
    } catch (err) {
      console.error('Error:', err);
    } finally {
      setLoading(false);
    }
  }, [studentId]);

  useEffect(() => {
    fetchReport();
  }, [fetchReport]);

  if (loading) return <div className="min-h-screen bg-gray-50 flex items-center justify-center">Loading...</div>;
  if (!report) return <div className="min-h-screen bg-gray-50 flex items-center justify-center">Not found</div>;

  // Calculate school year for display
  const getSchoolYearLabel = () => {
    if (schoolYearSettings?.school_year_start && schoolYearSettings?.school_year_end) {
      const startYear = new Date(schoolYearSettings.school_year_start).getFullYear();
      const endYear = new Date(schoolYearSettings.school_year_end).getFullYear();
      return `${startYear}-${endYear.toString().slice(-2)}`;
    }
    const now = new Date();
    const year = now.getMonth() >= 7 ? now.getFullYear() : now.getFullYear() - 1;
    return `${year}-${(year + 1).toString().slice(-2)}`;
  };

  // Generate report ID
  const reportId = `${new Date().getFullYear()}-${report.student.jurisdiction}-${studentId.slice(-3).toUpperCase()}`;

  // Group by subject
  const bySubject = report.work_items.reduce((acc, item) => {
    acc[item.subject] = acc[item.subject] || { minutes: 0, count: 0 };
    acc[item.subject].minutes += item.duration_minutes;
    acc[item.subject].count += 1;
    return acc;
  }, {});

  // Calculate monthly data for chart
  const getMonthlyData = () => {
    const months = ['SEP', 'OCT', 'NOV', 'DEC', 'JAN', 'FEB', 'MAR', 'APR', 'MAY'];
    const monthlyMinutes = {};
    
    report.work_items.forEach(item => {
      const date = new Date(item.date);
      const month = date.getMonth();
      // Map calendar months to school year months
      const monthNames = ['JAN', 'FEB', 'MAR', 'APR', 'MAY', 'JUN', 'JUL', 'AUG', 'SEP', 'OCT', 'NOV', 'DEC'];
      const monthKey = monthNames[month];
      monthlyMinutes[monthKey] = (monthlyMinutes[monthKey] || 0) + item.duration_minutes;
    });

    return months.map(month => ({
      month,
      minutes: monthlyMinutes[month] || 0,
      hours: Math.round((monthlyMinutes[month] || 0) / 60 * 10) / 10
    }));
  };

  const monthlyData = getMonthlyData();
  const maxMonthlyMinutes = Math.max(...monthlyData.map(d => d.minutes), 60);
  const avgWeeklyHours = report.total_hours > 0 ? Math.round((report.total_hours / (report.work_items.length > 0 ? Math.ceil(report.work_items.length / 5) : 1)) * 10) / 10 : 0;

  // Determine compliance status
  const isCompliant = compliance?.is_configured && 
    compliance.total_hours_logged >= (compliance.target_hours_total || 0);
  const targetHours = compliance?.target_hours_total || 900;

  // Get top standards as "key competencies"
  const topStandards = report.standards_summary.by_standard
    .sort((a, b) => b.count - a.count)
    .slice(0, 3);

  // PDF Export Function
  const exportPDF = async () => {
    setExporting(true);
    try {
      const doc = new jsPDF('p', 'mm', 'letter');
      const pageWidth = doc.internal.pageSize.getWidth();
      const pageHeight = doc.internal.pageSize.getHeight();
      const margin = 20;
      const contentWidth = pageWidth - (margin * 2);
      let y = margin;

      // Helper to add page number footer
      const addFooter = (pageNum, totalPages) => {
        doc.setFontSize(9);
        doc.setTextColor(128, 128, 128);
        doc.text(`Generated: ${new Date().toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })}`, margin, pageHeight - 10);
        doc.text(`Page ${pageNum} of ${totalPages}`, pageWidth - margin, pageHeight - 10, { align: 'right' });
      };

      // Header - App Name
      doc.setFontSize(24);
      doc.setTextColor(30, 58, 138); // indigo-900
      doc.setFont('helvetica', 'bold');
      doc.text('Homested', margin, y);
      y += 8;

      doc.setFontSize(11);
      doc.setTextColor(100, 100, 100);
      doc.setFont('helvetica', 'normal');
      doc.text('State Compliance Report', margin, y);
      y += 15;

      // Student Information
      doc.setFontSize(18);
      doc.setTextColor(31, 41, 55); // gray-800
      doc.setFont('helvetica', 'bold');
      doc.text(`${report.student.first_name} ${report.student.last_name}`, margin, y);
      y += 7;

      doc.setFontSize(11);
      doc.setTextColor(107, 114, 128); // gray-500
      doc.setFont('helvetica', 'normal');
      const jurisdiction = report.student.jurisdiction === 'MT' ? 'Montana' : report.student.jurisdiction;
      doc.text(`Grade ${report.student.grade_level} • ${jurisdiction} • School Year ${getSchoolYearLabel()}`, margin, y);
      y += 12;

      // Date Range
      const dateRangeStart = schoolYearSettings?.school_year_start 
        ? new Date(schoolYearSettings.school_year_start).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
        : 'Start';
      const dateRangeEnd = schoolYearSettings?.school_year_end 
        ? new Date(schoolYearSettings.school_year_end).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
        : 'End';
      
      doc.setFontSize(10);
      doc.setTextColor(75, 85, 99);
      doc.text(`Reporting Period: ${dateRangeStart} – ${dateRangeEnd}`, margin, y);
      y += 15;

      // Divider line
      doc.setDrawColor(229, 231, 235); // gray-200
      doc.setLineWidth(0.5);
      doc.line(margin, y, pageWidth - margin, y);
      y += 12;

      // Compliance Summary Section
      doc.setFontSize(12);
      doc.setTextColor(31, 41, 55);
      doc.setFont('helvetica', 'bold');
      doc.text('COMPLIANCE SUMMARY', margin, y);
      y += 10;

      // Compliance status box
      const statusText = isCompliant ? 'COMPLETE' : (compliance?.compliance_status === 'Behind' ? 'BEHIND' : 'ON TRACK');
      const statusColor = isCompliant ? [34, 197, 94] : (compliance?.compliance_status === 'Behind' ? [239, 68, 68] : [245, 158, 11]);
      
      doc.setFillColor(statusColor[0], statusColor[1], statusColor[2]);
      doc.roundedRect(margin, y, 30, 8, 2, 2, 'F');
      doc.setFontSize(9);
      doc.setTextColor(255, 255, 255);
      doc.setFont('helvetica', 'bold');
      doc.text(statusText, margin + 15, y + 5.5, { align: 'center' });

      doc.setFontSize(11);
      doc.setTextColor(31, 41, 55);
      doc.setFont('helvetica', 'normal');
      doc.text(`Total Minutes Logged: ${report.total_minutes.toLocaleString()} min (${report.total_hours} hours)`, margin + 35, y + 5.5);
      y += 12;

      doc.text(`Target Minutes: ${(targetHours * 60).toLocaleString()} min (${targetHours} hours)`, margin, y);
      y += 7;

      if (compliance?.is_configured) {
        doc.text(`Progress: ${compliance.percent_complete}% complete`, margin, y);
        y += 7;
        doc.text(`Days Remaining in School Year: ${compliance.days_remaining}`, margin, y);
      }
      y += 15;

      // Divider line
      doc.setDrawColor(229, 231, 235);
      doc.line(margin, y, pageWidth - margin, y);
      y += 12;

      // Instructional Log Table Header
      doc.setFontSize(12);
      doc.setTextColor(31, 41, 55);
      doc.setFont('helvetica', 'bold');
      doc.text('INSTRUCTIONAL LOG', margin, y);
      y += 10;

      // Table headers
      const colDate = margin;
      const colSubject = margin + 35;
      const colDuration = pageWidth - margin - 25;
      
      doc.setFontSize(9);
      doc.setTextColor(107, 114, 128);
      doc.setFont('helvetica', 'bold');
      doc.text('DATE', colDate, y);
      doc.text('SUBJECT', colSubject, y);
      doc.text('DURATION', colDuration, y);
      y += 3;

      doc.setDrawColor(209, 213, 219);
      doc.line(margin, y, pageWidth - margin, y);
      y += 5;

      // Table rows
      doc.setFont('helvetica', 'normal');
      doc.setTextColor(55, 65, 81);
      doc.setFontSize(10);

      // Sort work items by date
      const sortedItems = [...report.work_items].sort((a, b) => 
        new Date(a.date) - new Date(b.date)
      );

      let pageNum = 1;
      let totalPages = 1; // Will calculate after

      for (const item of sortedItems) {
        // Check if we need a new page
        if (y > pageHeight - 35) {
          addFooter(pageNum, '?');
          doc.addPage();
          pageNum++;
          y = margin;
          
          // Repeat table headers on new page
          doc.setFontSize(9);
          doc.setTextColor(107, 114, 128);
          doc.setFont('helvetica', 'bold');
          doc.text('DATE', colDate, y);
          doc.text('SUBJECT', colSubject, y);
          doc.text('DURATION', colDuration, y);
          y += 3;
          doc.setDrawColor(209, 213, 219);
          doc.line(margin, y, pageWidth - margin, y);
          y += 5;
          doc.setFont('helvetica', 'normal');
          doc.setTextColor(55, 65, 81);
          doc.setFontSize(10);
        }

        const dateFormatted = new Date(item.date + 'T00:00:00').toLocaleDateString('en-US', { 
          month: 'short', day: 'numeric', year: 'numeric' 
        });
        
        doc.text(dateFormatted, colDate, y);
        doc.text(item.subject || 'Other', colSubject, y);
        doc.text(`${item.duration_minutes} min`, colDuration, y);
        y += 6;
      }

      // If no items
      if (sortedItems.length === 0) {
        doc.setTextColor(156, 163, 175);
        doc.text('No activities logged', margin, y);
        y += 6;
      }

      // Final totals row
      y += 3;
      doc.setDrawColor(209, 213, 219);
      doc.line(margin, y, pageWidth - margin, y);
      y += 6;

      doc.setFont('helvetica', 'bold');
      doc.setTextColor(31, 41, 55);
      doc.text('TOTAL', colDate, y);
      doc.text(`${report.work_item_count} activities`, colSubject, y);
      doc.text(`${report.total_minutes} min`, colDuration, y);
      y += 15;

      // Evidence Section
      const allEvidence = sortedItems.flatMap(item => 
        (item.evidence || []).map(ev => ({
          ...ev,
          activityTitle: item.title,
          activityDate: item.date
        }))
      );

      if (allEvidence.length > 0) {
        // Check if we need a new page
        if (y > pageHeight - 60) {
          addFooter(pageNum, '?');
          doc.addPage();
          pageNum++;
          y = margin;
        }

        doc.setDrawColor(229, 231, 235);
        doc.line(margin, y, pageWidth - margin, y);
        y += 10;

        doc.setFontSize(12);
        doc.setTextColor(31, 41, 55);
        doc.setFont('helvetica', 'bold');
        doc.text('EVIDENCE ATTACHMENTS', margin, y);
        y += 8;

        doc.setFontSize(9);
        doc.setTextColor(107, 114, 128);
        doc.setFont('helvetica', 'normal');

        for (const ev of allEvidence) {
          // Check if we need a new page
          if (y > pageHeight - 25) {
            addFooter(pageNum, '?');
            doc.addPage();
            pageNum++;
            y = margin;
          }

          const label = ev.filename || ev.link_url || ev.file_reference || 'Attachment';
          const truncatedLabel = label.length > 60 ? label.substring(0, 57) + '...' : label;
          
          doc.setTextColor(55, 65, 81);
          doc.text(`• ${truncatedLabel}`, margin + 2, y);
          y += 5;
          
          if (ev.description) {
            doc.setTextColor(107, 114, 128);
            const truncatedDesc = ev.description.length > 70 ? ev.description.substring(0, 67) + '...' : ev.description;
            doc.text(`  ${truncatedDesc}`, margin + 4, y);
            y += 5;
          }
        }
      }

      // Add footer to last page
      totalPages = doc.internal.getNumberOfPages();
      for (let i = 1; i <= totalPages; i++) {
        doc.setPage(i);
        addFooter(i, totalPages);
      }

      // Save the PDF using blob for better browser compatibility
      const fileName = `Homested_Report_${report.student.first_name}_${report.student.last_name}_${new Date().toISOString().split('T')[0]}.pdf`;
      const pdfBlob = doc.output('blob');
      const url = URL.createObjectURL(pdfBlob);
      const link = document.createElement('a');
      link.href = url;
      link.download = fileName;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    } catch (err) {
      console.error('PDF export error:', err);
      alert('Failed to export PDF. Please try again.');
    } finally {
      setExporting(false);
    }
  };

  return (
    <div data-testid="report-page" className="min-h-screen bg-gray-100 pb-40">
      {/* Header */}
      <header className="bg-white border-b px-4 py-3 sticky top-0 z-10 print:hidden">
        <div className="flex items-center justify-between">
          <button onClick={() => navigate(-1)} className="p-1">
            <svg className="w-6 h-6 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
          </button>
          <h1 className="text-lg font-bold text-gray-800">State Compliance Report</h1>
          <button className="p-1">
            <svg className="w-6 h-6 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8.684 13.342C8.886 12.938 9 12.482 9 12c0-.482-.114-.938-.316-1.342m0 2.684a3 3 0 110-2.684m0 2.684l6.632 3.316m-6.632-6l6.632-3.316m0 0a3 3 0 105.367-2.684 3 3 0 00-5.367 2.684zm0 9.316a3 3 0 105.368 2.684 3 3 0 00-5.368-2.684z" />
            </svg>
          </button>
        </div>
      </header>

      {/* Preview Banner */}
      <div className="text-center py-2 text-xs text-gray-500 tracking-wider print:hidden">
        PREVIEWING {getSchoolYearLabel()} DOCUMENT
      </div>

      <main className="px-4">
        {/* Total Cumulative Hours Card */}
        <div 
          data-testid="compliance-hours-card"
          className="bg-gradient-to-br from-cyan-50 to-sky-50 rounded-xl p-4 mb-4 border border-sky-100"
        >
          <p className="text-xs font-semibold text-sky-600 tracking-wider mb-1">TOTAL CUMULATIVE HOURS</p>
          <div className="flex items-center justify-between">
            <div className="flex items-baseline gap-1">
              <span className="text-4xl font-bold text-gray-800">{report.total_hours}</span>
              <span className="text-gray-500">/ {targetHours} hours</span>
            </div>
            <div className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-sm font-semibold ${
              isCompliant ? 'bg-green-500 text-white' : 'bg-amber-100 text-amber-700'
            }`}>
              {isCompliant ? (
                <>
                  <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                  </svg>
                  COMPLIANT
                </>
              ) : (
                <>
                  <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm1-12a1 1 0 10-2 0v4a1 1 0 00.293.707l2.828 2.829a1 1 0 101.415-1.415L11 9.586V6z" clipRule="evenodd" />
                  </svg>
                  IN PROGRESS
                </>
              )}
            </div>
          </div>
          <p className="text-xs text-gray-500 mt-1">completed</p>
        </div>

        {/* Student Official Record Card */}
        <div className="bg-white rounded-xl shadow-sm p-4 mb-4">
          <div className="flex items-start justify-between">
            <div>
              <span className="inline-block bg-sky-100 text-sky-700 text-xs font-semibold px-2 py-0.5 rounded mb-2 tracking-wider">
                HOMESTED OFFICIAL RECORD
              </span>
              <h2 className="text-2xl font-bold text-gray-800">
                {report.student.first_name} {report.student.last_name}
              </h2>
              <p className="text-gray-500 text-sm">
                Grade {report.student.grade_level} • {report.student.jurisdiction === 'MT' ? 'Montana' : report.student.jurisdiction} • {getSchoolYearLabel()}
              </p>
            </div>
            <div className="text-right">
              <div className="w-12 h-12 rounded-full bg-gray-100 flex items-center justify-center mb-1">
                <svg className="w-6 h-6 text-gray-400" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M10 9a3 3 0 100-6 3 3 0 000 6zm-7 9a7 7 0 1114 0H3z" clipRule="evenodd" />
                </svg>
              </div>
              <p className="text-[10px] text-gray-400">REPORT ID:</p>
              <p className="text-xs font-semibold text-gray-600">{reportId}</p>
            </div>
          </div>
        </div>

        {/* Weekly Instructional Average Chart */}
        <div className="bg-white rounded-xl shadow-sm p-4 mb-4">
          <h3 className="font-bold text-gray-800 text-xs tracking-wider mb-3">WEEKLY INSTRUCTIONAL AVERAGE</h3>
          <div className="flex items-end justify-between h-24 gap-1">
            {monthlyData.map((d, i) => (
              <div key={d.month} className="flex flex-col items-center flex-1">
                <div className="w-full px-0.5">
                  <div
                    className="w-full rounded-t bg-sky-400 transition-all"
                    style={{ height: `${Math.max((d.minutes / maxMonthlyMinutes) * 70, 4)}px` }}
                  />
                </div>
                <span className="text-[10px] text-gray-400 mt-1">{d.month}</span>
              </div>
            ))}
          </div>
          <p className="text-xs text-gray-400 mt-2 text-center">
            Average: {avgWeeklyHours > 0 ? avgWeeklyHours : report.total_hours} hours / week
          </p>
        </div>

        {/* Detailed Instructional Log Table */}
        <div className="bg-white rounded-xl shadow-sm p-4 mb-4">
          <h3 className="font-bold text-gray-800 text-xs tracking-wider mb-3">DETAILED INSTRUCTIONAL LOG</h3>
          <table className="w-full text-sm">
            <thead>
              <tr className="text-xs text-gray-500 border-b">
                <th className="text-left pb-2 font-semibold">SUBJECT</th>
                <th className="text-right pb-2 font-semibold">HOURS</th>
                <th className="text-right pb-2 font-semibold">COUNT</th>
                <th className="text-right pb-2 font-semibold text-sky-600">TOTAL</th>
              </tr>
            </thead>
            <tbody>
              {Object.entries(bySubject).length > 0 ? (
                Object.entries(bySubject).map(([subject, data]) => {
                  const hours = Math.round(data.minutes / 60 * 10) / 10;
                  return (
                    <tr key={subject} className="border-b border-gray-100">
                      <td className="py-2.5 text-gray-700">{subject}</td>
                      <td className="py-2.5 text-right text-gray-600">{hours}h</td>
                      <td className="py-2.5 text-right text-gray-500">{data.count}</td>
                      <td className="py-2.5 text-right font-semibold text-sky-600">{hours}h</td>
                    </tr>
                  );
                })
              ) : (
                <tr>
                  <td colSpan={4} className="py-4 text-center text-gray-400">No activities logged yet</td>
                </tr>
              )}
              <tr className="font-semibold bg-gray-50">
                <td className="py-2.5 text-gray-800">Total Instructional</td>
                <td className="py-2.5 text-right text-gray-700">{report.total_hours}h</td>
                <td className="py-2.5 text-right text-gray-600">{report.work_item_count}</td>
                <td className="py-2.5 text-right text-sky-600">{report.total_hours}h</td>
              </tr>
            </tbody>
          </table>
        </div>

        {/* Key Competencies Mastered */}
        {topStandards.length > 0 && (
          <div className="bg-white rounded-xl shadow-sm p-4 mb-4 relative overflow-hidden">
            {/* Watermark checkmark */}
            <div className="absolute right-4 bottom-4 opacity-5">
              <svg className="w-32 h-32 text-green-500" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
              </svg>
            </div>
            
            <h3 className="font-bold text-gray-800 text-xs tracking-wider mb-3">KEY COMPETENCIES MASTERED</h3>
            <div className="space-y-3 relative z-10">
              {topStandards.map((std, i) => (
                <div key={i} className="flex items-start gap-3 bg-gray-50 rounded-lg p-3">
                  <div className="w-5 h-5 rounded-full bg-green-500 flex items-center justify-center flex-shrink-0 mt-0.5">
                    <svg className="w-3 h-3 text-white" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                    </svg>
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm text-gray-700 leading-snug">
                      {std.label}
                      <span className="text-gray-400 ml-1">({std.code})</span>
                    </p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Signature Section */}
        <div className="bg-white rounded-xl shadow-sm p-4 mb-4">
          <div className="flex justify-between items-end">
            <div className="flex-1">
              <div className="border-b border-gray-300 pb-2 mb-1">
                <p className="text-xl font-serif italic text-gray-700">
                  {report.student.last_name}
                </p>
              </div>
              <p className="text-[10px] text-gray-400 tracking-wider">TEACHER / PARENT SIGNATURE</p>
            </div>
            <div className="text-right ml-8">
              <p className="text-sm text-gray-700 mb-1">{new Date().toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })}</p>
              <p className="text-[10px] text-gray-400 tracking-wider">DATE OF SUBMISSION</p>
            </div>
          </div>
        </div>

        {/* Show All Activities Toggle */}
        <button
          data-testid="toggle-activities-btn"
          onClick={() => setShowActivities(!showActivities)}
          className="w-full bg-white rounded-xl shadow-sm p-3 mb-4 flex items-center justify-between text-gray-600 hover:bg-gray-50 print:hidden"
        >
          <span className="font-medium">All Activities ({report.work_item_count})</span>
          <svg className={`w-5 h-5 transition-transform ${showActivities ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
          </svg>
        </button>

        {/* Collapsible Activities List */}
        {showActivities && (
          <div className="bg-white rounded-xl shadow-sm p-4 mb-4 print:hidden">
            <h3 className="font-bold text-gray-800 text-xs tracking-wider mb-3">DETAILED ACTIVITIES</h3>
            <div className="space-y-3 max-h-96 overflow-y-auto">
              {report.work_items.map(item => (
                <div key={item.id} className="border-b border-gray-100 last:border-0 pb-3 last:pb-0">
                  <div className="flex justify-between items-start">
                    <div className="flex-1 min-w-0">
                      <p className="font-medium text-gray-800 truncate">{item.title}</p>
                      <p className="text-xs text-gray-500">{formatDate(item.date)} • {item.subject} • {item.duration_minutes}m</p>
                    </div>
                  </div>
                  {item.standards?.length > 0 && (
                    <div className="flex flex-wrap gap-1 mt-1">
                      {item.standards.map((s, i) => (
                        <span key={i} className="text-xs bg-indigo-100 text-indigo-700 px-1.5 py-0.5 rounded font-mono">{s.code}</span>
                      ))}
                    </div>
                  )}
                  {item.evidence?.length > 0 && (
                    <div className="mt-2 pl-2 border-l-2 border-amber-200">
                      <p className="text-xs text-amber-700 font-medium mb-1">Evidence ({item.evidence.length})</p>
                      {item.evidence.map((ev, i) => (
                        <div key={i} className="text-xs text-gray-600">
                          {ev.filename ? (
                            <a href={`${API_URL}${ev.file_url}`} target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline">
                              📎 {ev.filename}
                            </a>
                          ) : ev.link_url ? (
                            <a href={ev.link_url} target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline truncate block">
                              🔗 {ev.link_url}
                            </a>
                          ) : null}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}
      </main>

      {/* Action Buttons - Fixed at bottom */}
      <div className="fixed bottom-0 left-0 right-0 bg-white border-t px-4 py-3 space-y-2 print:hidden">
        <button
          data-testid="download-pdf-btn"
          onClick={exportPDF}
          disabled={exporting}
          className="w-full bg-sky-500 hover:bg-sky-600 disabled:bg-sky-300 text-white py-3.5 rounded-xl font-semibold flex items-center justify-center gap-2"
        >
          {exporting ? (
            <>
              <svg className="w-5 h-5 animate-spin" fill="none" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
              </svg>
              Generating PDF...
            </>
          ) : (
            <>
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
              </svg>
              Export PDF
            </>
          )}
        </button>
        <button
          data-testid="share-btn"
          className="w-full bg-white border-2 border-gray-200 hover:bg-gray-50 text-gray-700 py-3 rounded-xl font-semibold flex items-center justify-center gap-2"
        >
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
          </svg>
          Share with School District
        </button>
      </div>
    </div>
  );
};

// Settings Page
const SettingsPage = () => {
  const [config, setConfig] = useState(null);
  const [templates, setTemplates] = useState([]);
  const [students, setStudents] = useState([]);
  const [schoolYearSettings, setSchoolYearSettings] = useState(null);
  const [showAddTemplate, setShowAddTemplate] = useState(false);
  const [showAddStudent, setShowAddStudent] = useState(false);
  const [editingTemplate, setEditingTemplate] = useState(null);
  const [editingStudent, setEditingStudent] = useState(null);
  const [loading, setLoading] = useState(true);
  const [savingSettings, setSavingSettings] = useState(false);
  const navigate = useNavigate();

  const fetchData = useCallback(async () => {
    try {
      const [configRes, templatesRes, settingsRes, studentsRes] = await Promise.all([
        axios.get(`${API_URL}/api/config`),
        axios.get(`${API_URL}/api/templates`),
        axios.get(`${API_URL}/api/school-year-settings`),
        axios.get(`${API_URL}/api/students`)
      ]);
      setConfig(configRes.data);
      setTemplates(templatesRes.data);
      setSchoolYearSettings(settingsRes.data);
      setStudents(studentsRes.data);
    } catch (err) {
      console.error('Error:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleSaveSchoolYearSettings = async (field, value) => {
    setSavingSettings(true);
    try {
      await axios.put(`${API_URL}/api/school-year-settings`, { [field]: value });
      fetchData();
    } catch (err) {
      console.error('Error:', err);
    } finally {
      setSavingSettings(false);
    }
  };

  const handleDeleteTemplate = async (id) => {
    if (!window.confirm('Delete this template?')) return;
    try {
      await axios.delete(`${API_URL}/api/templates/${id}`);
      fetchData();
    } catch (err) {
      console.error('Error:', err);
    }
  };

  const handleSaveTemplate = async (data) => {
    try {
      if (editingTemplate) {
        await axios.put(`${API_URL}/api/templates/${editingTemplate.id}`, data);
      } else {
        await axios.post(`${API_URL}/api/templates`, data);
      }
      setShowAddTemplate(false);
      setEditingTemplate(null);
      fetchData();
    } catch (err) {
      console.error('Error:', err);
    }
  };

  const handleSaveStudent = async (data) => {
    try {
      if (editingStudent) {
        await axios.put(`${API_URL}/api/students/${editingStudent.id}`, data);
      } else {
        await axios.post(`${API_URL}/api/students`, data);
      }
      setShowAddStudent(false);
      setEditingStudent(null);
      fetchData();
    } catch (err) {
      console.error('Error:', err);
    }
  };

  const handleDeleteStudent = async (id) => {
    if (!window.confirm('Delete this student and all their activities? This cannot be undone.')) return;
    try {
      await axios.delete(`${API_URL}/api/students/${id}`);
      fetchData();
    } catch (err) {
      console.error('Error:', err);
    }
  };

  if (loading) return <div className="min-h-screen bg-gray-50 flex items-center justify-center">Loading...</div>;

  return (
    <div data-testid="settings-page" className="min-h-screen bg-gray-50 pb-20">
      <header className="bg-white border-b px-4 py-3">
        <h1 className="text-xl font-bold text-gray-800">Settings</h1>
      </header>

      <main className="px-4 py-4">
        {/* Students Section */}
        <div data-testid="students-settings" className="bg-white rounded-xl shadow-sm p-4 mb-4">
          <div className="flex justify-between items-center mb-3">
            <h2 className="font-semibold text-gray-800">Students</h2>
            <button
              data-testid="add-student-btn"
              onClick={() => { setEditingStudent(null); setShowAddStudent(true); }}
              className="text-indigo-600 text-sm font-medium"
            >
              + Add
            </button>
          </div>
          
          {students.length === 0 ? (
            <div className="text-center py-6">
              <p className="text-gray-400 text-sm mb-2">No students yet</p>
              <button
                onClick={() => setShowAddStudent(true)}
                className="text-indigo-600 text-sm font-medium"
              >
                Add your first student
              </button>
            </div>
          ) : (
            <div className="space-y-2">
              {students.map((s, i) => {
                const color = getStudentColor(i);
                return (
                  <div
                    key={s.id}
                    data-testid={`student-settings-${s.id}`}
                    className="flex items-center justify-between p-3 bg-gray-50 rounded-lg"
                  >
                    <div className="flex items-center gap-3">
                      <div className={`w-10 h-10 rounded-full ${color.bg} ${color.text} flex items-center justify-center font-semibold`}>
                        {s.first_name[0]}
                      </div>
                      <div>
                        <p className="font-medium text-gray-800">{s.first_name} {s.last_name}</p>
                        <p className="text-xs text-gray-500">Grade {s.grade_level} • {s.jurisdiction}</p>
                      </div>
                    </div>
                    <div className="flex gap-2">
                      <button
                        onClick={() => { setEditingStudent(s); setShowAddStudent(true); }}
                        className="text-gray-400 hover:text-indigo-600 p-1"
                      >
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                        </svg>
                      </button>
                      <button
                        onClick={() => handleDeleteStudent(s.id)}
                        className="text-gray-400 hover:text-red-500 p-1"
                      >
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                        </svg>
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* School Year Settings */}
        <div data-testid="school-year-settings" className="bg-white rounded-xl shadow-sm p-4 mb-4">
          <h2 className="font-semibold text-gray-800 mb-3">School Year</h2>
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-xs text-gray-500 mb-1">Start Date</label>
                <input
                  data-testid="school-year-start"
                  type="date"
                  value={schoolYearSettings?.school_year_start || ''}
                  onChange={(e) => handleSaveSchoolYearSettings('school_year_start', e.target.value)}
                  className="w-full border rounded-lg px-3 py-2 text-sm"
                />
              </div>
              <div>
                <label className="block text-xs text-gray-500 mb-1">End Date</label>
                <input
                  data-testid="school-year-end"
                  type="date"
                  value={schoolYearSettings?.school_year_end || ''}
                  onChange={(e) => handleSaveSchoolYearSettings('school_year_end', e.target.value)}
                  className="w-full border rounded-lg px-3 py-2 text-sm"
                />
              </div>
            </div>
            <div>
              <label className="block text-xs text-gray-500 mb-1">Target Hours (Total for Year)</label>
              <input
                data-testid="target-hours"
                type="number"
                value={schoolYearSettings?.target_minutes_total ? Math.round(schoolYearSettings.target_minutes_total / 60) : ''}
                onChange={(e) => handleSaveSchoolYearSettings('target_minutes_total', parseInt(e.target.value) * 60 || 0)}
                placeholder="e.g., 900"
                className="w-full border rounded-lg px-3 py-2 text-sm"
                min="0"
              />
              <p className="text-xs text-gray-400 mt-1">Montana requires ~720 hours for elementary</p>
            </div>
            {savingSettings && (
              <p className="text-xs text-indigo-600">Saving...</p>
            )}
            {schoolYearSettings?.is_configured && (
              <div className="bg-green-50 text-green-700 text-xs p-2 rounded flex items-center gap-2">
                <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                </svg>
                Compliance tracking active
              </div>
            )}
          </div>
        </div>

        {/* Lesson Templates */}
        <div className="bg-white rounded-xl shadow-sm p-4 mb-4">
          <div className="flex justify-between items-center mb-3">
            <h2 className="font-semibold text-gray-800">Lesson Templates</h2>
            <button
              data-testid="add-template-btn"
              onClick={() => setShowAddTemplate(true)}
              className="text-indigo-600 text-sm font-medium"
            >
              + Add
            </button>
          </div>
          
          {templates.length === 0 ? (
            <p className="text-gray-400 text-sm">No templates yet. Create templates for quick lesson starts.</p>
          ) : (
            <div className="space-y-2">
              {templates.map(t => (
                <div
                  key={t.id}
                  data-testid={`template-${t.id}`}
                  className="flex items-center justify-between p-3 bg-gray-50 rounded-lg"
                >
                  <div>
                    <p className="font-medium text-gray-800">{t.name}</p>
                    <p className="text-xs text-gray-500">
                      {t.subject} • {t.default_duration || 30}min
                      {t.default_standard_ids?.length > 0 && ` • ${t.default_standard_ids.length} standards`}
                    </p>
                  </div>
                  <div className="flex gap-2">
                    <button
                      onClick={() => { setEditingTemplate(t); setShowAddTemplate(true); }}
                      className="text-gray-400 hover:text-indigo-600"
                    >
                      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                      </svg>
                    </button>
                    <button
                      onClick={() => handleDeleteTemplate(t.id)}
                      className="text-gray-400 hover:text-red-500"
                    >
                      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                      </svg>
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Standards Library */}
        <div className="bg-white rounded-xl shadow-sm p-4 mb-4">
          <h2 className="font-semibold text-gray-800 mb-3">Standards Library</h2>
          <div className="text-sm text-gray-500">
            <p>Available: {config?.available_jurisdictions?.join(', ') || 'Montana'}</p>
            <p>Grades: {config?.available_grades?.join(', ') || '2'}</p>
            <p>Subjects: {config?.available_subjects?.join(', ') || 'ELA'}</p>
          </div>
        </div>

        {/* About */}
        <div className="bg-white rounded-xl shadow-sm p-4">
          <h2 className="font-semibold text-gray-800 mb-3">About</h2>
          <p className="text-sm text-gray-500">Homested v1.2</p>
          <p className="text-xs text-gray-400 mt-1">A calm, focused homeschool companion for parents.</p>
        </div>
      </main>

      <BottomNav />

      {showAddTemplate && (
        <TemplateModal
          template={editingTemplate}
          onClose={() => { setShowAddTemplate(false); setEditingTemplate(null); }}
          onSave={handleSaveTemplate}
        />
      )}

      {showAddStudent && (
        <StudentModal
          student={editingStudent}
          onClose={() => { setShowAddStudent(false); setEditingStudent(null); }}
          onSave={handleSaveStudent}
        />
      )}
    </div>
  );
};

// Student Modal (for Settings)
const StudentModal = ({ student, onClose, onSave }) => {
  const [formData, setFormData] = useState({
    first_name: student?.first_name || '',
    last_name: student?.last_name || '',
    grade_level: student?.grade_level || '2',
    jurisdiction: student?.jurisdiction || 'MT'
  });

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!formData.first_name.trim() || !formData.last_name.trim()) return;
    onSave(formData);
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-end sm:items-center justify-center z-50" onClick={onClose}>
      <div 
        data-testid="student-modal" 
        className="bg-white rounded-t-2xl sm:rounded-2xl shadow-xl p-6 w-full sm:max-w-md"
        onClick={e => e.stopPropagation()}
      >
        <h3 className="text-lg font-semibold mb-4">{student ? 'Edit Student' : 'Add Student'}</h3>
        <form onSubmit={handleSubmit}>
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">First Name</label>
                <input
                  data-testid="student-first-name"
                  type="text"
                  required
                  value={formData.first_name}
                  onChange={(e) => setFormData({ ...formData, first_name: e.target.value })}
                  className="w-full border rounded-lg px-3 py-2 focus:ring-2 focus:ring-indigo-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Last Name</label>
                <input
                  data-testid="student-last-name"
                  type="text"
                  required
                  value={formData.last_name}
                  onChange={(e) => setFormData({ ...formData, last_name: e.target.value })}
                  className="w-full border rounded-lg px-3 py-2 focus:ring-2 focus:ring-indigo-500"
                />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Grade</label>
                <select
                  data-testid="student-grade"
                  value={formData.grade_level}
                  onChange={(e) => setFormData({ ...formData, grade_level: e.target.value })}
                  className="w-full border rounded-lg px-3 py-2"
                >
                  {['K', '1', '2', '3', '4', '5', '6', '7', '8', '9', '10', '11', '12'].map(g => (
                    <option key={g} value={g}>Grade {g}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">State</label>
                <input
                  type="text"
                  value={formData.jurisdiction}
                  onChange={(e) => setFormData({ ...formData, jurisdiction: e.target.value })}
                  className="w-full border rounded-lg px-3 py-2"
                />
              </div>
            </div>
          </div>
          <div className="flex gap-3 mt-6">
            <button type="button" onClick={onClose} className="flex-1 py-2 text-gray-600 hover:bg-gray-100 rounded-lg">
              Cancel
            </button>
            <button data-testid="save-student-btn" type="submit" className="flex-1 py-2 bg-indigo-600 hover:bg-indigo-700 text-white rounded-lg">
              {student ? 'Save Changes' : 'Add Student'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

// Template Modal
const TemplateModal = ({ template, onClose, onSave }) => {
  const [name, setName] = useState(template?.name || '');
  const [subject, setSubject] = useState(template?.subject || 'ELA');
  const [defaultTitle, setDefaultTitle] = useState(template?.default_title || '');
  const [defaultNotes, setDefaultNotes] = useState(template?.default_notes || '');
  const [defaultDuration, setDefaultDuration] = useState(template?.default_duration || 30);
  const [selectedStandardIds, setSelectedStandardIds] = useState(template?.default_standard_ids || []);
  const [showStandardsPicker, setShowStandardsPicker] = useState(false);
  const [standards, setStandards] = useState([]);

  useEffect(() => {
    axios.get(`${API_URL}/api/standards?jurisdiction=Montana`).then(res => setStandards(res.data));
  }, []);

  const handleSubmit = () => {
    if (!name.trim()) return;
    onSave({
      name,
      subject,
      default_title: defaultTitle || null,
      default_notes: defaultNotes || null,
      default_duration: parseInt(defaultDuration) || 30,
      default_standard_ids: selectedStandardIds
    });
  };

  const toggleStandard = (id) => {
    setSelectedStandardIds(prev => 
      prev.includes(id) ? prev.filter(s => s !== id) : [...prev, id]
    );
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-end sm:items-center justify-center z-50" onClick={onClose}>
      <div 
        data-testid="template-modal"
        className="bg-white rounded-t-2xl sm:rounded-2xl shadow-xl p-6 w-full sm:max-w-md max-h-[90vh] overflow-y-auto"
        onClick={e => e.stopPropagation()}
      >
        <h3 className="text-lg font-semibold mb-4">{template ? 'Edit Template' : 'Create Template'}</h3>
        
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Template Name *</label>
            <input
              data-testid="template-name-input"
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="e.g., Morning Reading"
              className="w-full border rounded-lg px-3 py-2"
              required
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Subject</label>
            <select
              value={subject}
              onChange={(e) => setSubject(e.target.value)}
              className="w-full border rounded-lg px-3 py-2"
            >
              <option value="ELA">ELA</option>
              <option value="Math">Math</option>
              <option value="Science">Science</option>
              <option value="Social Studies">Social Studies</option>
              <option value="Other">Other</option>
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Default Title</label>
            <input
              type="text"
              value={defaultTitle}
              onChange={(e) => setDefaultTitle(e.target.value)}
              placeholder="Auto-fills when using this template"
              className="w-full border rounded-lg px-3 py-2"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Default Duration (minutes)</label>
            <input
              type="number"
              value={defaultDuration}
              onChange={(e) => setDefaultDuration(e.target.value)}
              placeholder="30"
              min="5"
              max="180"
              className="w-full border rounded-lg px-3 py-2"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Default Notes</label>
            <textarea
              value={defaultNotes}
              onChange={(e) => setDefaultNotes(e.target.value)}
              rows={2}
              className="w-full border rounded-lg px-3 py-2"
            />
          </div>

          <div>
            <div className="flex justify-between items-center mb-2">
              <label className="block text-sm font-medium text-gray-700">Default Standards</label>
              <button
                type="button"
                onClick={() => setShowStandardsPicker(!showStandardsPicker)}
                className="text-indigo-600 text-sm"
              >
                {showStandardsPicker ? 'Hide' : 'Select'}
              </button>
            </div>
            
            {selectedStandardIds.length > 0 && (
              <div className="flex flex-wrap gap-1 mb-2">
                {selectedStandardIds.map(id => {
                  const std = standards.find(s => s.id === id);
                  return std ? (
                    <span key={id} className="text-xs bg-indigo-100 text-indigo-700 px-2 py-1 rounded">
                      {std.code}
                    </span>
                  ) : null;
                })}
              </div>
            )}

            {showStandardsPicker && (
              <div className="max-h-40 overflow-y-auto border rounded-lg p-2 space-y-1">
                {standards.slice(0, 50).map(std => (
                  <div
                    key={std.id}
                    onClick={() => toggleStandard(std.id)}
                    className={`p-2 rounded cursor-pointer text-sm ${
                      selectedStandardIds.includes(std.id) 
                        ? 'bg-indigo-100 text-indigo-700' 
                        : 'hover:bg-gray-100'
                    }`}
                  >
                    <span className="font-mono">{std.code}</span>
                    <span className="text-gray-500 ml-2 text-xs">{std.label.slice(0, 50)}...</span>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        <div className="flex gap-3 mt-6">
          <button onClick={onClose} className="flex-1 py-2 text-gray-600 hover:bg-gray-100 rounded-lg">Cancel</button>
          <button 
            data-testid="save-template-btn"
            onClick={handleSubmit} 
            disabled={!name.trim()}
            className="flex-1 py-2 bg-indigo-600 hover:bg-indigo-700 text-white rounded-lg disabled:bg-gray-300"
          >
            {template ? 'Save' : 'Create'}
          </button>
        </div>
      </div>
    </div>
  );
};

// App
function App() {
  return (
    <Router>
      <Routes>
        <Route path="/" element={<TodayPage />} />
        <Route path="/journal" element={<JournalPage />} />
        <Route path="/reports-list" element={<ReportsListPage />} />
        <Route path="/settings" element={<SettingsPage />} />
        <Route path="/students/:studentId" element={<StudentDetailPage />} />
        <Route path="/work-items/:workItemId" element={<WorkItemDetailPage />} />
        <Route path="/reports/:studentId" element={<ReportPage />} />
      </Routes>
    </Router>
  );
}

export default App;
